import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { MenuButton, Icon, type MenuItem } from '../lib/Menu';
import { RoomEditor } from './RoomEditor';
import { wallSlopeFit, TRIM_LABEL, swapTrimJoint, trimCorner, trimJoint, isLegPart, trimOfPart, ROOM_ITEM_LABEL, flipRoomWall, lineGeom, assemblyBox, issueFixes, issueKey, mountOnWall, setAssemblyOriginAt, type Issue, DEFAULT_PIN, dividerSelectionError, makeDividers, frontWallOverlap, swapFrontWall, drawersFromShelves, shelvesInOpening, drawerOfPart, drawerOpeningError, frontPickError, makeDrawerSet, makeDivider, splitFront, splitShelvesByPanel, defaultPanel, EDGE_IDS, findOverlap, frontOfPart, frontShape, frontSpaces, HINGE_LABEL, makeFront, type FrontSpace, shelfOfPart, shelfOpeningError, makeShelfSetInSpace, type DrawerSet, type ShelfSet, flipPanel, swapOverlapInDoc, formatMm, nextPanelKey, panelPropertyOnAxis, partBox, type Pin, type Vec3, evaluateProject, migrateDocument, mm, type AssemblyInstance, type CabinetInstance, type EdgeId, type ProjectDocument } from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import type { ProjectSummaryDto } from '@scs/schemas';
import { Api, ApiError } from '../lib/api';
import { readJournal, clearJournal, type JournalEntry } from '../lib/journal';
import { navigate } from '../lib/router';
import { t } from '../lib/i18n';
import { Logo } from '../lib/ui';
import { TopbarUser, useSession } from '../App';
import { EditorStore, type SaveState, type Command } from './store';
import { ThreeViewport, type MoveTarget, type ViewName, type ViewOptions, type EdgeRef } from './viewport';
import { applyConnectedMoves, connectedMoves, pendingResize } from './connected';
import { LinkDialog, linkCandidates, linkEqual, linkLines, LinksSection, SHELF_PROPS } from './Links';
import { partWorldBox, roomPlanes, snapMove, unitWorldBox, worldDeltaToLocal, type SnapTarget, type WBox } from './snap';
import { panelKeyOfPart } from './AssemblyProperties';
import { CabinetProperties, PartProperties, ProjectProperties } from './Properties';
import { BottomPanel } from './BottomPanel';
import { LibraryPanel, usePresets } from './LibraryPanel';
import { PresetSection } from './PresetSection';
import { AssemblyProperties } from './AssemblyProperties';
import { SceneTree } from './SceneTree';
import { SaveToCatalog } from './SaveToCatalog';
import { LegProperties, SlopeProperties, TrimProperties } from './TrimProperties';
import { addItemToWall, roomEdit, removeRoomSelection, RoomProperties, ROOM_ITEM_ICON, ROOM_ITEM_KINDS } from './RoomProperties';
import { cornerLabel, itemLabel, lineLabel } from './roomLabels';
import { ShelfProperties } from './ShelfProperties';
import { DrawerProperties } from './DrawerProperties';
import { FrontGroupProperties, FrontProperties, HINGES } from './FrontProperties';
import { PresetWizard } from './PresetWizard';
import { instantiatePreset, loadVersion, newUnitId } from './presets';
import { CatalogContext, decorViews, useCatalog } from '../lib/catalog';

/** All placed units of a document (mutable references inside an exec draft). */
function allUnits(d: ProjectDocument): (CabinetInstance | AssemblyInstance)[] {
  return [...d.cabinets, ...d.assemblies];
}

interface Loaded {
  project: ProjectSummaryDto;
  role: string;
  revision: number;
  doc: ProjectDocument;
  journal: JournalEntry | null;
}

export function EditorPage({ projectId }: { projectId: string }) {
  const { me } = useSession();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await Api.project(projectId);
        const doc = migrateDocument(p.document);
        const j = (await readJournal(me.user.id, projectId)) ?? null;
        if (!cancelled) setLoaded({ project: p.project, role: p.role, revision: p.revision, doc, journal: j && JSON.stringify(j.document) !== JSON.stringify(doc) ? j : null });
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError && e.status === 404 ? 'Projekat ne postoji ili nemate pristup.' : (e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, me.user.id]);

  if (error)
    return (
      <div className="auth-page">
        <div className="card auth-card">
          <p className="error-text">{error}</p>
          <button className="btn" onClick={() => navigate('/')}>
            {t('editor.backToDash')}
          </button>
        </div>
      </div>
    );
  if (!loaded) return <div className="auth-page muted">…</div>;
  return <Editor loaded={loaded} userId={me.user.id} />;
}

const SAVE_LABEL: Record<SaveState, Parameters<typeof t>[0]> = {
  saved: 'save.saved',
  dirty: 'save.dirty',
  saving: 'save.saving',
  offline: 'save.offline',
  conflict: 'save.conflict',
  error: 'save.error',
  expired: 'save.expired',
  readonly: 'save.readonly',
};

function Editor({ loaded, userId }: { loaded: Loaded; userId: string }) {
  const readOnly = loaded.role === 'viewer';
  const store = useMemo(() => new EditorStore(loaded.project.id, userId, loaded.doc, loaded.revision, readOnly), [loaded, userId, readOnly]);
  useEffect(() => store.attach(), [store]);
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const { doc, selection } = state;
  const ev = useMemo(() => evaluateProject(doc), [doc]);
  /** Gizmo / point-snap subject: a panel of an assembly if one is selected, else the single selected unit. */
  const moveTarget = useMemo((): MoveTarget | null => {
    if (selection.cabinetIds.length !== 1 || (selection.extraPartIds ?? []).length) return null;
    const unitId = selection.cabinetIds[0]!;
    const asm = doc.assemblies.find((a) => a.id === unitId);
    if (asm?.locked || doc.cabinets.find((c) => c.id === unitId)?.locked) return null;
    if (asm && selection.partId?.startsWith(`${unitId}/`)) {
      const lk = selection.partId.split('/')[1]!;
      if (shelfOfPart(asm, lk) || frontOfPart(asm, lk) || drawerOfPart(asm, lk)) return null;
      return { kind: 'panel', unitId, partId: selection.partId };
    }
    return { kind: 'unit', unitId };
  }, [selection, doc]);
  const [journal, setJournal] = useState<JournalEntry | null>(loaded.journal);
  const [opts, setOpts] = useState<ViewOptions>({ grid: true, xray: false, exploded: false, labels: false, edges: false, openFronts: false, links: true, room: true });
  const [gizmoOn, setGizmoOn] = useState(true);
  const [snapInfo, setSnapInfo] = useState('');
  const [cornerState, setCornerState] = useState<'idle' | 'selected'>('idle');
  const [pinState, setPinState] = useState<'idle' | 'moving'>('idle');
  const [dupDrag, setDupDrag] = useState(false);
  const [edgePick, setEdgePick] = useState<{ source: EdgeRef | null; target: EdgeRef | null }>({ source: null, target: null });
  const [linkOpen, setLinkOpen] = useState(false);
  /** Room floor-plan editor ("Prostorija") open over the editor. */
  const [roomOpen, setRoomOpen] = useState(false);
  // Outliner visibility of parts, groups and room things (view only; units use their own `hidden`),
  // remembered per project in this browser.
  const hiddenKey = `scs.hidden.${loaded.project.id}`;
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(hiddenKey) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const toggleHidden = (k: string) =>
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      try {
        localStorage.setItem(hiddenKey, JSON.stringify([...n]));
      } catch {
        /* private mode */
      }
      return n;
    });
  /** Dimension lines (distance from the wall end and from the floor) of the items on the selected wall. */
  const [roomDims, setRoomDims] = useState(() => {
    try {
      return localStorage.getItem('scs.roomDims') !== '0';
    } catch {
      return true;
    }
  });
  const toggleRoomDims = () =>
    setRoomDims((v) => {
      try {
        localStorage.setItem('scs.roomDims', v ? '0' : '1');
      } catch {
        /* private mode */
      }
      return !v;
    });
  /** Unit waiting for a wall click ("Montiraj na zid"), and the note after mounting. */
  const [wallMount, setWallMount] = useState<string | null>(null);
  const [mountNote, setMountNote] = useState<string | null>(null);
  /** Right click on a corner of what moves: pick it for the snap, or put the assembly origin there. */
  const [cornerMenu, setCornerMenu] = useState<{ x: number; y: number; corner: Vec3 } | null>(null);
  /** The last gizmo move command (for typing the exact distance afterwards). */
  const lastMove = useRef<Command | null>(null);
  /** Percent typed per axis in the centre card. */
  const [centerPct, setCenterPct] = useState<Record<number, string>>({});
  /** "+ Police": openings of the assembly's front view; a click puts shelves into one. */
  const [regionPick, setRegionPick] = useState<{ unitId: string; spaces: FrontSpace[] } | null>(null);
  /** "+ Fronta": openings of the assembly's front view; Ctrl+click joins several into one front. */
  const [frontPick, setFrontPick] = useState<{
    unitId: string;
    spaces: FrontSpace[];
    selected: number[];
    betweenShelves: boolean;
    /** Several ordinary openings: one front over all of them, or one front each. Drawers always get one each. */
    together: boolean;
    error?: string;
  } | null>(null);
  /** "+ Pregrada": pick one opening, then the direction of the cut. */
  /** "+ Ladice": pick one opening (with side panels and a panel below / above), then the count. */
  const [drawerPick, setDrawerPick] = useState<{
    unitId: string;
    spaces: FrontSpace[];
    selected: number | null;
    count: number;
    /** Boards between the drawers. */
    dividers: boolean;
    /** The opening holds shelves: replace them, or keep them as boards between the drawers. */
    shelves: 'replace' | 'shelvesAsDividers';
  } | null>(null);
  const [dividerPick, setDividerPick] = useState<{ unitId: string; spaces: FrontSpace[]; selected: number[]; betweenShelves: boolean; count: number; error?: string } | null>(null);
  /** Inset fronts colliding with shelves: the user answered "Ne" for this state of the document. */
  const [shelfFitDismissed, setShelfFitDismissed] = useState<ProjectDocument | null>(null);
  // "Update connected": offered while the last commands resized panels and one of them is still selected.
  // Deselecting dismisses it for this document state (it comes back only with a new resize).
  const dismissedDoc = useRef<ProjectDocument | null>(null);
  const connected = useMemo(() => {
    if (readOnly || dismissedDoc.current === doc) return null;
    const pending = pendingResize(state.past, doc);
    if (!pending) return null;
    const selParts = [selection.partId, ...(selection.extraPartIds ?? [])].filter((x): x is string => !!x);
    const isSelected = pending.changed.some((c) => {
      const asm = doc.assemblies.find((a) => a.id === c.unitId);
      if (!asm) return false;
      if (selParts.length) return selParts.some((pid) => pid.startsWith(`${c.unitId}/`) && panelKeyOfPart(asm, pid.split('/')[1]!) === c.panelKey);
      return selection.cabinetIds.includes(c.unitId);
    });
    if (!isSelected) {
      dismissedDoc.current = doc;
      return null;
    }
    const moves = connectedMoves(pending.base, doc, pending.changed, undefined, ev);
    return moves.length ? moves : null;
  }, [state.past, doc, ev, selection, readOnly]);
  // "Copy dimension": length of the source edge → the target panel's dimension along the target edge.
  const copyDim = useMemo(() => {
    const { source, target } = edgePick;
    if (!source || !target || readOnly) return null;
    const partOf = (id: string) => {
      const unit = ev.cabinets.find((c) => c.instance.id === id.split('/')[0]);
      return unit && { unit, part: unit.result.parts.find((p) => p.id === id) };
    };
    const src = partOf(source.partId);
    const dst = partOf(target.partId);
    if (!src?.part || !dst?.part) return null;
    const value = partBox(src.part).size[source.axis];
    const current = partBox(dst.part).size[target.axis];
    const asm = doc.assemblies.find((a) => a.id === dst.unit.instance.id);
    const key = asm && panelKeyOfPart(asm, dst.part.localKey);
    const panel = key ? asm!.panels.find((p) => p.key === key) : undefined;
    const prop = panel && panelPropertyOnAxis(panel, target.axis);
    const PROP = { length: 'dužina', width: 'širina', thickness: 'debljina' } as const;
    return {
      value,
      current,
      label: dst.part.label,
      prop: prop ? PROP[prop] : null,
      blocked: !panel ? 'Ciljna ploča je dio parametarskog korpusa — mjera se mijenja u njegovim parametrima.' : asm!.locked ? 'Ciljni sklop je zaključan.' : null,
      apply: () => {
        if (!asm || !key || !prop) return;
        store.exec('Kopiranje dimenzije', (d) => {
          const p = d.assemblies.find((a) => a.id === asm.id)?.panels.find((x) => x.key === key);
          if (p) p[prop] = value;
        });
        vpRef.current?.clearEdges();
      },
    };
  }, [edgePick, ev, doc, readOnly, store]);
  const linkItems = useMemo(() => (readOnly ? [] : linkCandidates(doc, selection)), [doc, selection, readOnly]);
  const createShelves = (unitId: string, sp: FrontSpace) => {
    let id = '';
    store.exec('Nove police', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      if (shelfOpeningError(a, sp.ref)) return;
      const set = makeShelfSetInSpace(a, sp.ref);
      id = set.id;
      a.shelfSets = [...(a.shelfSets ?? []), set];
    });
    if (id) store.select({ cabinetIds: [unitId], partId: null, shelfSetId: id });
  };
  const cancelRegionPick = () => {
    setRegionPick(null);
    vpRef.current?.setRegionPick(null);
  };
  const frontUnit = selection.cabinetIds.length === 1 ? doc.assemblies.find((a) => a.id === selection.cabinetIds[0]) : undefined;
  /** Shows the openings of the selected assembly as pickable boxes (click picks, Ctrl+click adds). */
  const showOpenings = (betweenShelves: boolean): { unitId: string; spaces: FrontSpace[] } | null => {
    if (!frontUnit || frontUnit.locked) return null;
    cancelRegionPick();
    const spaces = frontSpaces(frontUnit, { betweenShelves });
    const zf = assemblyBox(frontUnit).max[2];
    vpRef.current?.setRegionPick(
      frontUnit.id,
      spaces.map((sp) => ({ min: [sp.rect.x0, sp.rect.y0, zf - mm(12)] as Vec3, max: [sp.rect.x1, sp.rect.y1, zf + mm(4)] as Vec3 })),
    );
    return { unitId: frontUnit.id, spaces };
  };
  const startShelves = () => {
    setDividerPick(null);
    setFrontPick(null);
    setDrawerPick(null);
    const o = showOpenings(false);
    if (o) setRegionPick(o);
  };
  const startDrawers = () => {
    setDividerPick(null);
    setFrontPick(null);
    const o = showOpenings(false);
    if (o) setDrawerPick({ ...o, selected: null, count: 3, dividers: false, shelves: 'replace' });
  };
  const cancelDrawers = () => {
    setDrawerPick(null);
    vpRef.current?.setRegionPick(null);
  };
  const createDrawers = () => {
    if (!drawerPick || drawerPick.selected === null) return;
    const { unitId, spaces, selected, count, dividers, shelves } = drawerPick;
    let id = '';
    store.exec('Nove ladice', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      const ref = spaces[selected]!.ref;
      if (drawerOpeningError(a, ref)) return;
      const shelfSet = shelvesInOpening(a, ref);
      if (shelfSet) {
        id = drawersFromShelves(a, shelfSet, shelves, count, dividers).id;
      } else {
        const set = { ...makeDrawerSet(a, ref), count, ...(dividers && count > 1 ? { dividerThickness: mm(18) } : {}) };
        id = set.id;
        a.drawerSets = [...(a.drawerSets ?? []), set];
      }
    });
    cancelDrawers();
    if (id) store.select({ cabinetIds: [unitId], partId: null, drawerSetId: id });
  };
  const startFront = (betweenShelves = false) => {
    setDividerPick(null);
    setDrawerPick(null);
    const o = showOpenings(betweenShelves);
    if (o) setFrontPick({ ...o, selected: [], betweenShelves, together: true });
  };
  const cancelFrontPick = () => {
    setFrontPick(null);
    vpRef.current?.setRegionPick(null);
  };
  const startDivider = (betweenShelves = false) => {
    setFrontPick(null);
    setDrawerPick(null);
    const o = showOpenings(betweenShelves);
    if (o) setDividerPick({ ...o, selected: [], betweenShelves, count: dividerPick?.count ?? 1 });
  };
  const dividerSize = (pick: { spaces: FrontSpace[]; selected: number[] }) => {
    const rs = pick.selected.map((i) => pick.spaces[i]!.rect);
    const w = Math.max(...rs.map((r) => r.x1)) - Math.min(...rs.map((r) => r.x0));
    const h = Math.max(...rs.map((r) => r.y1)) - Math.min(...rs.map((r) => r.y0));
    return `${(w / 1000).toFixed(1)} × ${(h / 1000).toFixed(1)} mm`;
  };
  const cancelDivider = () => {
    setDividerPick(null);
    vpRef.current?.setRegionPick(null);
  };
  /** Splits the selected front in two; the original stays selected as the left / bottom part. */
  const doSplitFront = (unitId: string, frontId: string, direction: 'vertical' | 'horizontal') =>
    store.exec(direction === 'vertical' ? 'Podjela fronte lijevo/desno' : 'Podjela fronte gore/dolje', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId);
      if (a && !a.locked) splitFront(a, frontId, direction);
    });
  const createDivider = (direction: 'vertical' | 'horizontal') => {
    if (!dividerPick) return;
    const { unitId, spaces, selected } = dividerPick;
    const asm = doc.assemblies.find((x) => x.id === unitId);
    const picked = selected.map((i) => spaces[i]!);
    const error = asm ? dividerSelectionError(asm, picked, spaces) : 'Sklop ne postoji.';
    if (error) return setDividerPick({ ...dividerPick, error: `Selekcija nije pravilna: ${error}` });
    let key = '';
    const n = dividerPick.count;
    store.exec(direction === 'vertical' ? (n > 1 ? `Nove pregrade (${n})` : 'Nova pregrada') : n > 1 ? `Nove fiksne police (${n})` : 'Nova fiksna polica', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      const panels = makeDividers(a, picked, direction, n);
      key = panels[0]!.key;
      for (const panel of panels) {
        a.panels.push(panel);
        // Shelves in the cut space follow: split left/right, or the part above gets its own set.
        splitShelvesByPanel(a, panel);
      }
    });
    cancelDivider();
    // The new panel stays selected (gizmo / centring) — afterwards it is an ordinary panel.
    if (key) store.select({ cabinetIds: [unitId], partId: `${unitId}/${key}` });
  };
  // Inset fronts of the selected assembly that run into shelves: offer to shorten those shelves.
  const shelfFit = useMemo(() => {
    if (readOnly || !frontUnit || shelfFitDismissed === doc) return null;
    const unit = ev.cabinets.find((c) => c.instance.id === frontUnit.id);
    if (!unit) return null;
    const hits: { setId: string; index: number; cut: number }[] = [];
    for (const f of frontUnit.fronts ?? []) {
      if (f.mode !== 'inset') continue;
      const fp = unit.result.parts.find((p) => p.localKey === f.id);
      if (!fp) continue;
      const fb = partBox(fp);
      for (const p of unit.result.parts) {
        const sh = shelfOfPart(frontUnit, p.localKey);
        if (!sh) continue;
        const b = partBox(p);
        const ov = [0, 1, 2].map((k) => Math.min(b.min[k]! + b.size[k]!, fb.min[k]! + fb.size[k]!) - Math.max(b.min[k]!, fb.min[k]!));
        if (ov.every((o) => o > 500)) hits.push({ setId: sh.set.id, index: sh.index, cut: b.min[2] + b.size[2] - fb.min[2] });
      }
    }
    return hits.length ? { unitId: frontUnit.id, hits } : null;
  }, [frontUnit, ev, doc, readOnly, shelfFitDismissed]);
  const fitShelves = () => {
    if (!shelfFit) return;
    store.exec('Prilagođavanje polica fronti', (d) => {
      const a = d.assemblies.find((x) => x.id === shelfFit.unitId);
      for (const h of shelfFit.hits) {
        const set = a?.shelfSets?.find((x) => x.id === h.setId);
        if (!set) continue;
        set.setbacks = { ...(set.setbacks ?? {}), [String(h.index)]: (set.setbacks?.[String(h.index)] ?? 0) + h.cut };
      }
    });
  };

  /** Picked openings split into drawers (always one front each) and ordinary openings. */
  const frontGroups = (pick: NonNullable<typeof frontPick>) => {
    const picked = pick.selected.map((i) => pick.spaces[i]!);
    const drawers = picked.filter((sp) => sp.ref.drawer);
    const plain = picked.filter((sp) => !sp.ref.drawer);
    return { drawers, plain, groups: [...drawers.map((sp) => [sp]), ...(pick.together && plain.length ? [plain] : plain.map((sp) => [sp]))] };
  };
  /** Ordinary openings that make one piece (each next one touches one already taken). */
  const onePiece = (spaces: FrontSpace[]) => {
    const taken = spaces.slice(0, 1);
    let rest = spaces.slice(1);
    for (let grew = true; grew && rest.length; ) {
      grew = false;
      for (const sp of rest)
        if (!frontPickError(taken, sp)) {
          taken.push(sp);
          rest = rest.filter((x) => x !== sp);
          grew = true;
        }
    }
    return rest.length === 0;
  };
  const frontPreview = useMemo(() => {
    if (!frontPick || !frontPick.selected.length) return null;
    const asm = doc.assemblies.find((a) => a.id === frontPick.unitId);
    const { plain } = frontGroups(frontPick);
    // Size / CNC preview of the front over the ordinary openings (one opening, or several joined).
    if (!asm || !plain.length || (!frontPick.together && plain.length > 1)) return null;
    return frontShape(asm, { spaces: plain.map((sp) => sp.ref), mode: 'overlay', thickness: mm(18) });
  }, [frontPick, doc]);
  const createFront = () => {
    if (!frontPick || !frontPick.selected.length) return;
    const { unitId } = frontPick;
    const { plain, groups } = frontGroups(frontPick);
    if (frontPick.together && plain.length > 1 && !onePiece(plain)) {
      return setFrontPick({ ...frontPick, error: 'Izabrani prostori se ne dodiruju — ne mogu biti jedna fronta. Izaberite „Svaki zasebno”.' });
    }
    let id = '';
    store.exec(groups.length > 1 ? 'Nove fronte' : 'Nova fronta', (d) => {
      const a = d.assemblies.find((x) => x.id === unitId)!;
      for (const g of groups) {
        const f = makeFront(a, g);
        id ||= f.id;
        a.fronts = [...(a.fronts ?? []), f];
      }
    });
    cancelFrontPick();
    if (id) store.select({ cabinetIds: [unitId], partId: `${unitId}/${id}` });
  };

  useEffect(() => {
    if (!vpRef.current) return;
    // Openings that cannot be picked are shown red and ignore the click.
    vpRef.current.regionGuard = (i, additive) => {
      if (drawerPick) {
        const asm = doc.assemblies.find((a) => a.id === drawerPick.unitId);
        return !!asm && !drawerOpeningError(asm, drawerPick.spaces[i]!.ref);
      }
      if (dividerPick) return !dividerPick.spaces[i]!.ref.drawer;
      if (regionPick) {
        const asm = doc.assemblies.find((a) => a.id === regionPick.unitId);
        return !!asm && !shelfOpeningError(asm, regionPick.spaces[i]!.ref);
      }
      return true;
    };
    vpRef.current.onRegionPick = (i, additive) => {
      if (drawerPick) {
        setDrawerPick({ ...drawerPick, selected: i });
        vpRef.current?.setRegionSelected([i]);
        return;
      }
      if (dividerPick) {
        const selected = additive ? (dividerPick.selected.includes(i) ? dividerPick.selected.filter((x) => x !== i) : [...dividerPick.selected, i]) : [i];
        setDividerPick({ ...dividerPick, selected, error: undefined });
        vpRef.current?.setRegionSelected(selected);
        return;
      }
      if (frontPick) {
        const selected = additive ? (frontPick.selected.includes(i) ? frontPick.selected.filter((x) => x !== i) : [...frontPick.selected, i]) : [i];
        setFrontPick({ ...frontPick, selected, error: undefined });
        vpRef.current?.setRegionSelected(selected);
        return;
      }
      if (!regionPick) return;
      const sp = regionPick.spaces[i]!;
      cancelRegionPick();
      createShelves(regionPick.unitId, sp);
    };
  });
  const shelfItems = linkItems.length >= 2 && linkItems.every((i) => i.kind === 'shelves') ? linkItems : null;

  // Centre between neighbours: per axis, the nearest panels/units on both sides that face the moved one.
  const centerOptions = useMemo(() => {
    if (readOnly || !moveTarget) return [];
    const unit = ev.cabinets.find((c) => c.instance.id === moveTarget.unitId);
    if (!unit) return [];
    const part = moveTarget.kind === 'panel' ? unit.result.parts.find((p) => p.id === moveTarget.partId) : undefined;
    const me = part ? partWorldBox(unit, part) : unitWorldBox(unit);
    const others: WBox[] = [];
    for (const c of ev.cabinets) {
      if (c.instance.hidden) continue;
      if (moveTarget.kind === 'panel') {
        for (const p of c.result.parts) if (p.id !== moveTarget.partId) others.push(partWorldBox(c, p));
      } else if (c.instance.id !== unit.instance.id) others.push(unitWorldBox(c));
    }
    const out: { axis: 0 | 1 | 2; lo: number; hi: number; pct: number; label: string }[] = [];
    for (const k of [0, 1, 2] as const) {
      // A neighbour must face at least half of the moved object (thin parts touching it, such as
      // shelves resting against a divider, are not the space it is centred in).
      const overlaps = (b: WBox) => [0, 1, 2].every((j) => j === k || Math.min(b.max[j]!, me.max[j]!) - Math.max(b.min[j]!, me.min[j]!) >= Math.max(500, (me.max[j]! - me.min[j]!) / 2));
      let lo = -Infinity;
      let hi = Infinity;
      for (const b of others) {
        if (!overlaps(b)) continue;
        if (b.max[k]! <= me.min[k]! + 500) lo = Math.max(lo, b.max[k]!);
        if (b.min[k]! >= me.max[k]! - 500) hi = Math.min(hi, b.min[k]!);
      }
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo <= 0) continue;
      const centre = (me.min[k]! + me.max[k]!) / 2;
      out.push({ axis: k, lo, hi, pct: Math.round(((centre - lo) / (hi - lo)) * 1000) / 10, label: k === 1 ? 'između donje i gornje' : 'između lijeve i desne' });
    }
    return out;
  }, [moveTarget, ev, readOnly]);
  // Typed percentages belong to the selected object.
  useEffect(() => setCenterPct({}), [moveTarget?.unitId, moveTarget?.kind === 'panel' ? moveTarget.partId : null]);
  const applyCenter = (axis: 0 | 1 | 2, pct: number) => {
    const o = centerOptions.find((x) => x.axis === axis);
    const vp = vpRef.current;
    if (!o || !moveTarget || !vp) return;
    const unit = ev.cabinets.find((c) => c.instance.id === moveTarget.unitId)!;
    const part = moveTarget.kind === 'panel' ? unit.result.parts.find((p) => p.id === moveTarget.partId) : undefined;
    const me = part ? partWorldBox(unit, part) : unitWorldBox(unit);
    const target = o.lo + ((o.hi - o.lo) * pct) / 100;
    const d = Math.round(target - (me.min[axis] + me.max[axis]) / 2);
    if (d === 0) return;
    const delta: Vec3 = [0, 0, 0];
    delta[axis] = d;
    vp.onMove(moveTarget, delta);
  };

  // Issue clicked in the bottom panel: camera at the spot, fixes offered in a card.
  const [issueFocus, setIssueFocus] = useState<Issue | null>(null);
  const sameIssue = (a: Issue, b: Issue) => a.code === b.code && a.message === b.message && a.cabinetId === b.cabinetId;
  const focusIssue = (i: Issue) => {
    setIssueFocus(i);
    if (i.cabinetId) store.select({ cabinetIds: [i.cabinetId, ...(i.otherCabinetId ? [i.otherCabinetId] : [])], partId: i.partId ?? null, extraPartIds: i.otherPartId ? [i.otherPartId] : [] });
    vpRef.current?.focusIssue([i.partId, i.otherPartId].filter((x): x is string => !!x), [i.cabinetId, i.otherCabinetId].filter((x): x is string => !!x));
  };
  const closeIssue = () => {
    setIssueFocus(null);
    vpRef.current?.clearIssueMarker();
  };
  // Fixed (or changed by any edit): the card and the red box go away.
  useEffect(() => {
    if (issueFocus && !ev.issues.some((i) => sameIssue(i, issueFocus))) closeIssue();
  }, [ev, issueFocus]);
  const fixes = useMemo(() => (issueFocus && !readOnly ? issueFixes(doc, ev, issueFocus) : []), [issueFocus, doc, ev, readOnly]);

  // A front and a wall picked together (Ctrl+click): swap who covers the edge on that side only.
  const frontWall = useMemo(() => {
    const ids = [selection.partId, ...(selection.extraPartIds ?? [])].filter((x): x is string => !!x);
    if (readOnly || ids.length !== 2) return null;
    const [u0, k0] = ids[0]!.split('/') as [string, string];
    const [u1, k1] = ids[1]!.split('/') as [string, string];
    const asm = u0 === u1 ? doc.assemblies.find((a) => a.id === u0) : undefined;
    if (!asm || asm.locked) return null;
    const front = frontOfPart(asm, k0) ?? frontOfPart(asm, k1);
    const panelKey = asm.panels.find((p) => p.key === (front?.id === k0 ? k1 : k0))?.key;
    if (!front || !panelKey) return null;
    const state = frontWallOverlap(asm, front.id, panelKey);
    if (!state) return { error: 'Ova ploča nije uz otvor fronte, ili je fronta unutarnja.' as const, front, panel: asm.panels.find((p) => p.key === panelKey)! };
    return { state, unitId: asm.id, front, panel: asm.panels.find((p) => p.key === panelKey)! };
  }, [selection, doc, readOnly]);

  // Two panels of one assembly meeting at a corner: swap which one runs through.
  const overlap = useMemo(() => {
    const [a, b] = linkItems;
    if (linkItems.length !== 2 || a!.kind !== 'panel' || b!.kind !== 'panel' || a!.unitId !== b!.unitId) return null;
    const asm = doc.assemblies.find((x) => x.id === a!.unitId);
    const pa = asm?.panels.find((p) => p.key === a!.panelKey);
    const pb = asm?.panels.find((p) => p.key === b!.panelKey);
    const o = asm && !asm.locked && pa && pb ? findOverlap(pa, pb) : null;
    if (!o) return null;
    const label = (k: string) => asm!.panels.find((p) => p.key === k)!.label;
    return {
      text: `prolazi: ${label(o.through)} · naslanja se: ${label(o.butting)}`,
      apply: () =>
        store.exec(
          'Zamjena preklopa',
          (d) => {
            swapOverlapInDoc(d, asm!.id, pa!.key, pb!.key);
          },
          { pins: false },
        ),
    };
  }, [linkItems, doc, store]);
  // Two fillers / plinth of one assembly meeting at a corner: swap which one runs through.
  const trimSwap = useMemo(() => {
    const ids = [selection.partId, ...(selection.extraPartIds ?? [])].filter((x): x is string => !!x);
    if (readOnly || ids.length !== 2) return null;
    const [u0, k0] = ids[0]!.split('/') as [string, string];
    const [u1, k1] = ids[1]!.split('/') as [string, string];
    const asm = u0 === u1 ? doc.assemblies.find((a) => a.id === u0) : undefined;
    if (!asm || asm.locked) return null;
    const s0 = trimOfPart(asm, k0);
    const s1 = trimOfPart(asm, k1);
    const corner = s0 && s1 ? trimCorner(s0, s1) : null;
    if (!corner) return null;
    const [v, h] = corner.split('-') as ['top' | 'bottom', 'left' | 'right'];
    const through = trimJoint(asm, corner) === 'h' ? v : h;
    return { unitId: asm.id, corner, text: `prolazi: ${TRIM_LABEL[through]} · naslanja se: ${TRIM_LABEL[through === v ? h : v]}` };
  }, [selection, doc, readOnly]);
  const sHeld = useRef(false);
  const [projection, setProjection] = useState<'perspective' | 'ortho'>('perspective');
  const [leftTab, setLeftTab] = useState<'scene' | 'library'>('scene');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [catalogSave, setCatalogSave] = useState<string | null>(null);
  const [presetsKey, setPresetsKey] = useState(0);
  const { items: presets, error: presetsError } = usePresets(presetsKey);
  const { catalog } = useCatalog();
  const [insertError, setInsertError] = useState<string | null>(null);

  const hostRef = useRef<HTMLDivElement>(null);
  const vpRef = useRef<ThreeViewport | null>(null);

  useEffect(() => {
    const vp = new ThreeViewport(hostRef.current!);
    vpRef.current = vp;
    (window as unknown as { __scsViewport?: ThreeViewport }).__scsViewport = vp;
    return () => {
      vp.dispose();
      vpRef.current = null;
    };
  }, []);

  useEffect(() => {
    vpRef.current!.onPick = (hit, mod) => {
      if (!hit) return store.select({ cabinetIds: [], partId: null });
      const sel = store.getState().selection;
      if (mod.ctrl) {
        // Ctrl+click: add/remove a single part (e.g. several panels for linking).
        const all = [sel.partId, ...(sel.extraPartIds ?? [])].filter((x): x is string => !!x);
        const next = all.includes(hit.partId) ? all.filter((x) => x !== hit.partId) : [...all, hit.partId];
        const units = [...new Set([...sel.cabinetIds, hit.cabinetId])];
        return store.select({ cabinetIds: units, partId: next[0] ?? null, extraPartIds: next.slice(1) });
      }
      if (mod.shift) {
        const cur = sel.cabinetIds;
        return store.select({ cabinetIds: cur.includes(hit.cabinetId) ? cur.filter((c) => c !== hit.cabinetId) : [...cur, hit.cabinetId], partId: null });
      }
      store.select({ cabinetIds: [hit.cabinetId], partId: hit.partId });
    };
    vpRef.current!.onRoomPick = (room) => store.select({ cabinetIds: [], partId: null, room });
    vpRef.current!.onRoomItemMove = (itemId, u0, elevation) => {
      const plan = store.getState().doc.roomPlan;
      const it = plan?.items.find((i) => i.id === itemId);
      if (!plan || !it) return;
      roomEdit(store.exec.bind(store), `Pomjeranje: ${itemLabel(plan, it)}`, (p) => {
        const x = p.items.find((i) => i.id === itemId);
        const l = x && p.lines.find((y) => y.id === x.lineId);
        const g = l && lineGeom(p, l);
        if (!x || !g) return;
        x.offset = x.from === 'a' ? Math.round(u0) : Math.round(g.length - u0 - x.width);
        x.elevation = Math.round(elevation);
      });
      store.select({ cabinetIds: [], partId: null, room: { lineId: it.lineId, itemId } });
    };
    vpRef.current!.onCornerState = setCornerState;
    vpRef.current!.onPinState = setPinState;
    vpRef.current!.onDuplicateDrag = setDupDrag;
    vpRef.current!.onEdgePick = setEdgePick;
  }, [store]);

  // Parts hidden in the outliner: single parts and whole groups (shelves, drawers, fronts).
  const hiddenParts = useMemo(() => {
    const out = new Set<string>();
    for (const c of ev.cabinets)
      for (const p of c.result.parts) {
        if (hidden.has(p.id)) out.add(p.id);
        const asm = doc.assemblies.find((a) => a.id === c.instance.id);
        if (!asm) continue;
        const sh = shelfOfPart(asm, p.localKey);
        const dr = drawerOfPart(asm, p.localKey);
        if ((sh && hidden.has(`${asm.id}#shelf:${sh.set.id}`)) || (dr && hidden.has(`${asm.id}#drawer:${dr.set.id}`)) || (frontOfPart(asm, p.localKey) && hidden.has(`${asm.id}#fronts`))) out.add(p.id);
      }
    return out;
  }, [ev, doc, hidden]);
  const edgeMap = useMemo(() => {
    const m = new Map<string, Record<EdgeId, string>>();
    for (const c of ev.cabinets) for (const pe of c.edges) m.set(pe.partId, Object.fromEntries(EDGE_IDS.map((e) => [e, pe.decisions[e].decision])) as Record<EdgeId, string>);
    return m;
  }, [ev]);

  const firstFrame = useRef(true);
  useEffect(() => {
    const vp = vpRef.current!;
    const setParts = selection.shelfSetId ? ev.cabinets.find((c) => c.instance.id === selection.cabinetIds[0])?.result.parts.filter((p) => p.localKey.startsWith(`${selection.shelfSetId}-`)).map((p) => p.id) ?? [] : [];
    vp.setSelection(selection.cabinetIds, selection.partId, [...(selection.extraPartIds ?? []), ...setParts]);
    vp.setRoomSelection(selection.room ?? null);
    vp.setRoomEditing({ dims: roomDims, editable: !readOnly });
    vp.setDecors(decorViews(catalog));
    vp.setScene(doc, ev, edgeMap, { ...opts, hiddenParts, hiddenRoom: hidden });
    vp.setLinkLines(linkLines(doc, ev.cabinets), opts.links);
    // No gizmo while picking openings (it would catch the clicks).
    vp.setGizmo(gizmoOn && !readOnly && !frontPick && !regionPick && !dividerPick && !drawerPick ? moveTarget : null);
    // A selected window / socket …: gizmo turned with its wall, only in the wall plane.
    vp.setRoomGizmo(gizmoOn && !readOnly && !!selection.room?.itemId);
    // Corner snap: hover a corner of the selected panel, click it, then click a corner of another panel.
    vp.setMoveTarget(readOnly ? null : moveTarget, selection.partId);
    vp.setCornersEnabled(!opts.exploded && !opts.openFronts);
    // Pin (anchor point) of the selected panel or parametric cabinet.
    const pinInfo = ((): { pin: Pin; size: Vec3 | null } | null => {
      if (readOnly || !moveTarget || opts.exploded || opts.openFronts) return null;
      if (moveTarget.kind === 'panel') {
        const asm = doc.assemblies.find((a) => a.id === moveTarget.unitId);
        const key = asm && panelKeyOfPart(asm, moveTarget.partId.split('/')[1]!);
        const panel = asm?.panels.find((x) => x.key === key);
        return panel ? { pin: panel.pin ?? DEFAULT_PIN, size: null } : null;
      }
      const cab = doc.cabinets.find((c) => c.id === moveTarget.unitId);
      const evc = ev.cabinets.find((c) => c.instance.id === moveTarget.unitId);
      return cab && evc ? { pin: cab.pin ?? DEFAULT_PIN, size: evc.result.bounds.size } : null;
    })();
    vp.setPin(pinInfo ? moveTarget : null, pinInfo?.pin ?? null, pinInfo?.size ?? null);
    // Flip orientation next to the gizmo: free panels about X/Y/Z, parametric cabinets/assemblies about Y.
    vp.setFlipAxes(!gizmoOn || readOnly || !moveTarget ? [] : moveTarget.kind === 'panel' ? ['x', 'y', 'z'] : ['y']);
    if (firstFrame.current) {
      firstFrame.current = false;
      vp.setView('persp');
    }
  }, [doc, ev, edgeMap, opts, selection, catalog, gizmoOn, moveTarget, readOnly, frontPick, regionPick, dividerPick, drawerPick, roomDims, hiddenParts, hidden]);

  // Snap while dragging the gizmo (S held) and committing the move as one undoable command.
  useEffect(() => {
    const vp = vpRef.current!;
    vp.onSnap = (target, raw) => {
      if (!sHeld.current) {
        setSnapInfo('');
        return { delta: raw.map((v) => Math.round(v / 1000) * 1000) as Vec3, info: '' }; // 1 mm steps
      }
      const unit = ev.cabinets.find((c) => c.instance.id === target.unitId);
      if (!unit) return { delta: raw, info: '' };
      let moving;
      const targets: SnapTarget[] = [];
      if (target.kind === 'panel') {
        const part = unit.result.parts.find((p) => p.id === target.partId)!;
        moving = partWorldBox(unit, part);
        for (const p of unit.result.parts) if (p.id !== part.id) targets.push({ box: partWorldBox(unit, p), label: p.label });
      } else {
        moving = unitWorldBox(unit);
      }
      for (const c of ev.cabinets) if (c.instance.id !== unit.instance.id && !c.instance.hidden) targets.push({ box: unitWorldBox(c), label: c.instance.name });
      const r = snapMove(moving, raw, targets, roomPlanes(doc));
      setSnapInfo(r.hits.map((h) => h.label).join(' · '));
      return { delta: r.delta, info: '' };
    };
    vp.onPinChange = (target, pin) => {
      store.exec('Pomjeranje pina', (d) => {
        if (target.kind === 'panel') {
          const asm = d.assemblies.find((a) => a.id === target.unitId);
          const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
          const panel = asm?.panels.find((x) => x.key === key);
          if (panel) panel.pin = pin;
        } else {
          const cab = d.cabinets.find((c) => c.id === target.unitId);
          if (cab) cab.pin = pin;
        }
      });
    };
    vp.onFlip = (target, axis) => {
      if (target.kind === 'unit') {
        store.exec('Okretanje 90°', (d) => {
          const u = [...d.cabinets, ...d.assemblies].find((x) => x.id === target.unitId);
          if (u && !u.locked) u.transform.rotationY = ((u.transform.rotationY + 90) % 360) as typeof u.transform.rotationY;
        });
        return;
      }
      const asm = doc.assemblies.find((a) => a.id === target.unitId);
      const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
      if (!asm || !key) return;
      store.exec('Okretanje ploče', (d) => {
        const a = d.assemblies.find((x) => x.id === target.unitId)!;
        if (a.locked) return;
        const i = a.panels.findIndex((x) => x.key === key);
        a.panels[i] = flipPanel(a.panels[i]!, axis);
      });
    };
    const move = (target: MoveTarget, delta: Vec3, opts?: { duplicate: boolean }) => {
      setSnapInfo('');
      const unit = ev.cabinets.find((c) => c.instance.id === target.unitId);
      if (!unit) return;
      if (opts?.duplicate) {
        // Alt + gizmo drag: the original stays, a copy lands at the new position (one undo step).
        if (target.kind === 'unit') {
          store.exec('Kopiranje', (d) => {
            const cab = d.cabinets.find((c) => c.id === target.unitId);
            const src = cab ?? d.assemblies.find((a) => a.id === target.unitId);
            if (!src) return;
            const copy = structuredClone(src);
            copy.id = newUnitId(d, cab ? 'k' : 's');
            copy.name = `${src.name} (kopija)`;
            copy.locked = false;
            copy.transform.position = [src.transform.position[0] + delta[0], src.transform.position[1] + delta[1], src.transform.position[2] + delta[2]];
            if (cab) d.cabinets.push(copy as CabinetInstance);
            else d.assemblies.push(copy as AssemblyInstance);
            queueMicrotask(() => store.select({ cabinetIds: [copy.id], partId: null }));
          });
        } else {
          const local = worldDeltaToLocal(unit, delta);
          const asm = doc.assemblies.find((a) => a.id === target.unitId);
          const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
          if (!asm || !key) return;
          store.exec('Kopiranje ploče', (d) => {
            const a = d.assemblies.find((x) => x.id === target.unitId)!;
            if (a.locked) return;
            const src = a.panels.find((x) => x.key === key)!;
            const copy = structuredClone(src);
            copy.key = nextPanelKey(a);
            copy.label = `${src.label} (kopija)`;
            copy.position = [src.position[0] + local[0], src.position[1] + local[1], src.position[2] + local[2]];
            a.panels.push(copy);
            const partKey = copy.repeat && copy.repeat.count > 1 ? `${copy.key}-1` : copy.key;
            queueMicrotask(() => store.select({ cabinetIds: [a.id], partId: `${a.id}/${partKey}` }));
          });
        }
        return;
      }
      if (target.kind === 'unit') {
        store.exec('Pomjeranje', (d) => {
          const u = [...d.cabinets, ...d.assemblies].find((x) => x.id === target.unitId);
          if (!u || u.locked) return;
          u.transform.position = [u.transform.position[0] + delta[0], u.transform.position[1] + delta[1], u.transform.position[2] + delta[2]];
        });
      } else {
        const local = worldDeltaToLocal(unit, delta);
        const asm = doc.assemblies.find((a) => a.id === target.unitId);
        const key = asm && panelKeyOfPart(asm, target.partId.split('/')[1]!);
        if (!asm || !key) return;
        store.exec('Pomjeranje ploče', (d) => {
          const a = d.assemblies.find((x) => x.id === target.unitId)!;
          if (a.locked) return;
          const pnl = a.panels.find((x) => x.key === key)!;
          pnl.position = [pnl.position[0] + local[0], pnl.position[1] + local[1], pnl.position[2] + local[2]];
        });
      }
    };
    vp.onMove = (target, delta, opts) => {
      move(target, delta, opts);
      lastMove.current = opts?.duplicate ? null : (store.getState().past.at(-1) ?? null);
    };
    // Exact distance typed after a drag: redo that drag with the typed delta (one undo step), or
    // move by the difference if something else happened in between.
    vp.onExactMove = (target, from, to) => {
      const st = store.getState();
      const last = st.past.at(-1);
      if (last && last === lastMove.current && st.doc === last.after) {
        store.undo();
        move(target, to);
      } else {
        move(target, [to[0] - from[0], to[1] - from[1], to[2] - from[2]]);
      }
      lastMove.current = store.getState().past.at(-1) ?? null;
    };
  }, [ev, doc, store]);

  // Warn before closing with unsaved server state (the journal still keeps a local copy).
  useEffect(() => {
    const on = (e: BeforeUnloadEvent) => {
      if (state.doc !== state.serverDoc) e.preventDefault();
    };
    window.addEventListener('beforeunload', on);
    return () => window.removeEventListener('beforeunload', on);
  }, [state.doc, state.serverDoc]);

  const selCab = selection.cabinetIds.length === 1 ? doc.cabinets.find((c) => c.id === selection.cabinetIds[0]) : undefined;
  const selAsm = selection.cabinetIds.length === 1 ? doc.assemblies.find((a) => a.id === selection.cabinetIds[0]) : undefined;
  const selUnit = selCab ?? selAsm;
  /** "Montiraj na zid": waiting for a wall click for this unit. */
  const roomHasWalls = !!doc.roomPlan?.wallHeight && doc.roomPlan.lines.some((l) => l.kind === 'wall') && opts.room;
  const canMount = !readOnly && !!selUnit && !selUnit.locked && !selection.partId && roomHasWalls;
  useEffect(() => {
    if (!wallMount) return vpRef.current?.setWallPick(null);
    vpRef.current?.setWallPick((lineId) => {
      const r = mountOnWall(doc, ev, wallMount, lineId);
      setWallMount(null);
      if ('error' in r) return setMountNote(r.error);
      store.exec('Montiraj na zid', (d) => {
        const u = d.cabinets.find((c) => c.id === wallMount) ?? d.assemblies.find((a) => a.id === wallMount);
        if (!u) return;
        u.transform.rotationY = r.rotationY;
        u.transform.position = r.position;
      });
      setMountNote(r.warning ?? null);
    });
  }, [wallMount, doc, ev, store]);
  useEffect(() => {
    if (wallMount && (!selUnit || selUnit.id !== wallMount)) setWallMount(null);
  }, [selUnit, wallMount]);
  useEffect(() => {
    if (vpRef.current) vpRef.current.onCornerMenu = (at, corner) => setCornerMenu({ ...at, corner });
  });
  useEffect(() => {
    if (!cornerMenu) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      if (e.target instanceof HTMLElement && e.target.closest('.corner-menu')) return;
      setCornerMenu(null);
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', close, true);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', close, true);
    };
  }, [cornerMenu]);
  const selEval = selUnit ? ev.cabinets.find((c) => c.instance.id === selUnit.id) : undefined;
  // A selected assembly standing at a sloped wall and reaching above it: offer the exact slope.
  const slopeFit = useMemo(() => (selAsm && !selection.partId && !readOnly && !selAsm.locked ? wallSlopeFit(doc, ev, selAsm.id) : null), [selAsm, selection.partId, readOnly, doc, ev]);
  const selPart = selEval && selection.partId ? selEval.result.parts.find((p) => p.id === selection.partId) : undefined;
  const selFront = ((): { asm: NonNullable<typeof selAsm>; front: NonNullable<ReturnType<typeof frontOfPart>> } | null => {
    const f = selAsm && selPart ? frontOfPart(selAsm, selPart.localKey) : null;
    return f && selAsm ? { asm: selAsm, front: f } : null;
  })();
  /** Selected drawer set: a drawer part in the scene, or the set row in the outliner. */
  const selDrawer = ((): { set: DrawerSet; index: number | null } | null => {
    if (!selAsm) return null;
    const hit = selPart ? drawerOfPart(selAsm, selPart.localKey) : null;
    if (hit) return hit;
    const set = selection.drawerSetId ? selAsm.drawerSets?.find((x) => x.id === selection.drawerSetId) : undefined;
    return set ? { set, index: null } : null;
  })();
  /** Selected shelf set: a single shelf picked in the scene, or the set row in the outliner. */
  const selShelf = ((): { set: ShelfSet; index: number | null } | null => {
    if (!selAsm) return null;
    const hit = selPart ? shelfOfPart(selAsm, selPart.localKey) : null;
    if (hit) return { set: hit.set, index: hit.index };
    const set = selection.shelfSetId ? selAsm.shelfSets?.find((x) => x.id === selection.shelfSetId) : undefined;
    return set ? { set, index: null } : null;
  })();

  const actions = {
    duplicate: () => {
      if (!selection.cabinetIds.length) return;
      store.exec('Dupliranje', (d) => {
        const ids: string[] = [];
        for (const id of selection.cabinetIds) {
          const evSrc = ev.cabinets.find((c) => c.instance.id === id);
          const cab = d.cabinets.find((c) => c.id === id);
          const asm = d.assemblies.find((a) => a.id === id);
          const src = cab ?? asm;
          if (!src || !evSrc) continue;
          const copy = structuredClone(src);
          copy.id = newUnitId(d, cab ? 'k' : 's');
          copy.name = `${src.name} (kopija)`;
          copy.transform.position[0] += evSrc.result.bounds.size[0];
          if (cab) d.cabinets.push(copy as CabinetInstance);
          else d.assemblies.push(copy as AssemblyInstance);
          ids.push(copy.id);
        }
        queueMicrotask(() => store.select({ cabinetIds: ids, partId: null }));
      });
    },
    remove: () => {
      if (!selection.cabinetIds.length) return;
      store.exec('Brisanje', (d) => {
        d.cabinets = d.cabinets.filter((c) => !selection.cabinetIds.includes(c.id) || c.locked);
        d.assemblies = d.assemblies.filter((a) => !selection.cabinetIds.includes(a.id) || a.locked);
      });
    },
    mirror: () =>
      store.exec('Ogledalo', (d) => {
        for (const c of allUnits(d)) if (selection.cabinetIds.includes(c.id) && !c.locked) c.transform.mirrored = !c.transform.mirrored;
      }),
    rotate: () =>
      store.exec('Rotacija 90°', (d) => {
        for (const c of allUnits(d)) if (selection.cabinetIds.includes(c.id) && !c.locked) c.transform.rotationY = (((c.transform.rotationY + 90) % 360) as 0 | 90 | 180 | 270);
      }),
    toggle: (key: 'hidden' | 'locked') =>
      store.exec(key === 'hidden' ? 'Sakrij/prikaži' : 'Zaključaj/otključaj', (d) => {
        for (const c of allUnits(d)) if (selection.cabinetIds.includes(c.id)) c[key] = !c[key];
      }),
    addPanel: () => {
      const target = selAsm;
      if (target) {
        const used = new Set(target.panels.map((p) => p.key));
        let n = target.panels.length + 1;
        while (used.has(`ploca-${n}`)) n++;
        const key = `ploca-${n}`;
        // Place the new panel on top of the existing ones so it never starts inside another panel.
        const top = ev.cabinets.find((c) => c.instance.id === target.id)?.result.bounds.size[1] ?? 0;
        store.exec('Dodaj ploču', (d) => {
          d.assemblies.find((a) => a.id === target.id)!.panels.push({ ...defaultPanel(key), label: `Ploča ${n}`, position: [0, top, 0] });
        });
        store.select({ cabinetIds: [target.id], partId: `${target.id}/${key}` });
        return;
      }
      let id = '';
      store.exec('Novi sklop od ploča', (d) => {
        id = newUnitId(d, 's');
        const maxX = ev.cabinets.reduce((m, c) => Math.max(m, c.instance.transform.position[0] + c.result.bounds.size[0]), 0);
        d.assemblies.push({
          id,
          name: `${id.toUpperCase()} Sklop`,
          panels: [{ ...defaultPanel('ploca-1'), label: 'Ploča 1' }],
          transform: { position: [maxX, 0, 0], rotationY: 0, mirrored: false },
          materials: {},
          edgeOverrides: {},
          hidden: false,
          locked: false,
          environment: 'dry' as const,
          preset: null,
        });
      });
      store.select({ cabinetIds: [id], partId: `${id}/ploca-1` });
    },
    insertPreset: async (p: PresetSummaryDto) => {
      setInsertError(null);
      try {
        const content = await loadVersion(p.latestVersionId);
        const r = instantiatePreset(store.getState().doc, p, p.latestVersionId, p.latestVersion, content, ev.cabinets);
        store.exec(`Ubaci preset: ${p.name}`, (d) => {
          if (r.kind === 'cabinet') d.cabinets.push(r.unit);
          else d.assemblies.push(r.unit);
        });
        store.select({ cabinetIds: [r.unit.id], partId: null });
      } catch (e) {
        setInsertError((e as Error).message);
      }
    },
    nudge: (dx: number, dz: number) =>
      store.exec('Pomjeranje', (d) => {
        for (const c of allUnits(d)) {
          if (!selection.cabinetIds.includes(c.id) || c.locked) continue;
          c.transform.position[0] += dx;
          c.transform.position[2] += dz;
        }
      }),
  };

  // Keyboard shortcuts
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest?.('input, select, textarea')) return;
      if (e.key === 's' || e.key === 'S') {
        if (!(e.ctrlKey || e.metaKey)) {
          sHeld.current = true;
          return;
        }
      }
      if (e.key === 'Escape' && wallMount) return setWallMount(null);
      if (e.key === 'Escape' && vpRef.current?.isRegionPicking()) return (cancelRegionPick(), cancelFrontPick(), cancelDivider(), cancelDrawers());
      if (e.key === 'Escape' && vpRef.current?.isPinMoving()) return vpRef.current.cancelPinMove();
      if (e.key === 'Escape' && vpRef.current?.hasEdgeSelection()) return vpRef.current.clearEdges();
      if (e.key === 'Escape' && vpRef.current?.hasCornerSelection()) return vpRef.current.clearCorner();
      if ((e.key === 'l' || e.key === 'L') && canEdit && linkCandidates(doc, selection).length >= 2) return setLinkOpen(true);
      if (e.key === 'g' || e.key === 'G') return setGizmoOn((g) => !g);
      const mod = e.ctrlKey || e.metaKey;
      const step = mm(e.shiftKey ? 100 : 10);
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) (e.preventDefault(), store.undo());
      else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) (e.preventDefault(), store.redo());
      else if (mod && e.key.toLowerCase() === 's') (e.preventDefault(), void store.saveNow());
      else if (mod && e.key.toLowerCase() === 'd') (e.preventDefault(), actions.duplicate());
      else if (e.key === 'Delete' && selection.room?.pointId) return;
      else if (e.key === 'Delete' && selection.room && canEdit && doc.roomPlan) (removeRoomSelection(store.exec.bind(store), doc.roomPlan, selection.room), store.select({ cabinetIds: [], partId: null, room: selection.room.itemId ? { lineId: selection.room.lineId } : null }));
      else if (e.key === 'Delete') actions.remove();
      else if (e.key === 'f' || e.key === 'F') vpRef.current?.frameSelection();
      else if (e.key === '1') vpRef.current?.setView('front');
      else if (e.key === '3') vpRef.current?.setView('right');
      else if (e.key === '7') vpRef.current?.setView('top');
      else if (e.key === '0') vpRef.current?.setView('persp');
      else if (e.key === 'x' || e.key === 'X') setOpts((o) => ({ ...o, xray: !o.xray }));
      else if (e.key === 'e' || e.key === 'E') setOpts((o) => ({ ...o, edges: !o.edges }));
      else if (e.key === 'Escape') store.select({ cabinetIds: [], partId: null });
      else if (e.key.startsWith('Arrow') && selection.cabinetIds.length) {
        e.preventDefault();
        const map: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        const [dx, dz] = map[e.key]!;
        actions.nudge(dx, dz);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === 's' || e.key === 'S') {
        sHeld.current = false;
        setSnapInfo('');
      }
    };
    const blur = () => (sHeld.current = false);
    window.addEventListener('keydown', on);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', on);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  });

  const toggle = (k: keyof ViewOptions) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  const flipProjection = () => {
    const next = projection === 'perspective' ? 'ortho' : 'perspective';
    setProjection(next);
    vpRef.current?.setProjection(next);
  };
  /** Small icon button with a tooltip (toolbar and the view bar in the viewport). */
  const ib = (icon: string, title: string, onClick: () => void, o: { on?: boolean; disabled?: boolean; danger?: boolean } = {}) => (
    <button className={`btn sm icon ${o.on ? 'on' : 'ghost'} ${o.danger ? 'danger' : ''}`} aria-label={title} aria-pressed={o.on} title={title} disabled={o.disabled} onClick={onClick}>
      <Icon name={icon} />
    </button>
  );
  const VIEWS: { v: ViewName; label: string; icon: string; key?: string }[] = [
    { v: 'persp', label: '3D', icon: 'view-3d', key: '0' },
    { v: 'front', label: t('editor.front'), icon: 'view-front', key: '1' },
    { v: 'back', label: t('editor.back'), icon: 'view-back' },
    { v: 'left', label: t('editor.left'), icon: 'view-left' },
    { v: 'right', label: t('editor.right'), icon: 'view-right', key: '3' },
    { v: 'top', label: t('editor.top'), icon: 'view-top', key: '7' },
    { v: 'bottom', label: 'Dolje', icon: 'view-top' },
  ];
  const DISPLAY: { k: keyof ViewOptions; label: string; icon: string; key?: string; title?: string }[] = [
    { k: 'grid', label: t('editor.grid'), icon: 'grid' },
    { k: 'xray', label: t('editor.xray'), icon: 'xray', key: 'X' },
    { k: 'exploded', label: t('editor.exploded'), icon: 'explode' },
    { k: 'labels', label: t('editor.labels'), icon: 'id-labels' },
    { k: 'edges', label: t('editor.edgeView'), icon: 'show-edges', key: 'E' },
    { k: 'openFronts', label: 'Otvorene fronte', icon: 'open-fronts', title: 'Pregled otvaranja — ne mijenja proizvodnu geometriju' },
    { k: 'links', label: 'Linkovi', icon: 'links', title: 'Prikaz linkova (isprekidane linije)' },
    { k: 'rawCut', label: 'Stvarni rez', icon: 'raw-cut', title: 'Ploče sklopa pod kosinom kao sirove daske (prije kosih rezova, s nadmjerom) — kako se režu' },
  ];
  const viewMenu: MenuItem[] = [
    { kind: 'check', label: t('editor.perspective'), icon: 'perspective', checked: projection === 'perspective', title: 'Bez kvačice: ortogonalna projekcija', onToggle: flipProjection },
    { kind: 'sub', label: 'Pogled', icon: 'view-3d', items: VIEWS.map((x) => ({ label: x.label, icon: x.icon, shortcut: x.key, onSelect: () => vpRef.current?.setView(x.v) })) },
    { label: t('editor.frame'), icon: 'views-more', shortcut: 'F', onSelect: () => vpRef.current?.frameSelection() },
    { kind: 'sep' },
    ...DISPLAY.map((x): MenuItem => ({ kind: 'check', label: x.label, shortcut: x.key, title: x.title, checked: !!opts[x.k], onToggle: () => toggle(x.k) })),
  ];
  const insideNeedsUnit = !canEditUnit();
  function canEditUnit() {
    return !readOnly && !!frontUnit && !frontUnit.locked;
  }
  const addMenu: MenuItem[] = [
    {
      kind: 'sub',
      label: 'Element',
      icon: 'add-cabinet',
      items: [
        { label: 'Iz kataloga…', icon: 'catalog', onSelect: () => setLeftTab('library') },
        { label: 'Nova ploča (u sklop ili novi sklop)', icon: 'add-panel', disabled: readOnly, onSelect: actions.addPanel },
        { label: 'Preset iz selekcije…', icon: 'preset', disabled: readOnly || !selection.cabinetIds.length, onSelect: () => setWizardOpen(true) },
      ],
    },
    { label: 'Ploča', icon: 'add-panel', disabled: readOnly, onSelect: actions.addPanel },
    { kind: 'sep' },
    {
      kind: 'sub',
      label: 'Unutrašnjost',
      icon: 'add-divider',
      items: [
        { label: 'Pregrada / fiksna polica', icon: 'add-divider', disabled: insideNeedsUnit, title: 'Presijeci prostor novom pločom', onSelect: () => startDivider(false) },
        { label: 'Police', icon: 'add-shelves', disabled: insideNeedsUnit, title: 'Kliknite prostor u koji idu police', onSelect: startShelves },
        { label: 'Ladice', icon: 'add-drawer', disabled: insideNeedsUnit, onSelect: startDrawers },
      ],
    },
    { label: 'Fronta', icon: 'add-front', disabled: insideNeedsUnit, onSelect: () => startFront(false) },
  ];
  const hasSel = selection.cabinetIds.length > 0;
  const selRoom = selection.room && doc.roomPlan?.lines.some((l) => l.id === selection.room!.lineId) ? selection.room : null;
  const selRoomItem = selRoom?.itemId ? doc.roomPlan?.items.find((i) => i.id === selRoom.itemId) : undefined;
  const canEdit = !readOnly;
  const hasRoom = !!doc.roomPlan?.lines.length;
  const roomMenu: MenuItem[] = [
    { label: hasRoom ? 'Uredi tlocrt prostorije' : 'Kreiraj prostoriju', icon: 'view-top', disabled: readOnly && !hasRoom, title: 'Tlocrt: crtanje zidova i vodilja, prozori, vrata, utičnice, odvod, dovod vode', onSelect: () => setRoomOpen(true) },
    { kind: 'check', label: 'Prikaži zidove', checked: opts.room, disabled: !doc.roomPlan?.wallHeight, onToggle: () => toggle('room') },
    {
      label: 'Izvezi zidove (OBJ)',
      icon: 'cut-list',
      disabled: !doc.roomPlan?.wallHeight || !opts.room,
      title: 'Zidovi (i plafon ako je prikazan) kao OBJ — metri, Y gore, svaki zid poseban objekat',
      onSelect: () => {
        const plan = doc.roomPlan;
        if (!plan) return;
        const names = new Map(plan.lines.filter((l) => l.kind === 'wall').map((l) => [l.id, lineLabel(plan, l.id)]));
        const obj = vpRef.current?.roomObj(names);
        if (!obj) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([obj], { type: 'text/plain' }));
        a.download = `${loaded.project.name.replace(/[^\w.-]+/g, '_')}-zidovi.obj`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      },
    },
    { kind: 'sep' },
    {
      label: 'Obriši prostoriju',
      icon: 'delete',
      disabled: readOnly || !doc.roomPlan,
      onSelect: () =>
        store.exec('Brisanje prostorije', (d) => {
          delete d.roomPlan;
        }),
    },
  ];

  return (
    <CatalogContext.Provider value={catalog}>
    <div className="editor">
      <header className="topbar">
        <button className="btn ghost sm" onClick={() => navigate('/')} title={t('editor.backToDash')}>
          <Logo size={18} /> ←
        </button>
        <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loaded.project.name}</strong>
        {loaded.project.isDemo && <span className="badge demo">DEMO</span>}
        <span className="faint">rev {state.baseRevision}</span>
        <div className={`save-state ${state.save}`} role="status" aria-live="polite" title={state.saveError ?? undefined}>
          <span className="dot" />
          {t(SAVE_LABEL[state.save])}
        </div>
        <div className="spacer" />
        <TopbarUser />
      </header>
      <div className="toolbar" role="toolbar">
        <MenuButton label="Dodaj" items={addMenu} />
        <MenuButton label="Prikaz" items={viewMenu} />
        <MenuButton label="Prostorija" items={roomMenu} />
        <span className="sep" />
        <button className="btn sm primary" disabled={!canEdit || state.doc === state.serverDoc || state.save === 'saving'} onClick={() => void store.saveNow()} title={`${t('save.save')} (Ctrl+S)`}>
          <Icon name="save" />
          {t('save.save')}
        </button>
        {ib('undo', `${t('editor.undo')} (Ctrl+Z)${state.past.at(-1) ? `: ${state.past.at(-1)!.label}` : ''}`, () => store.undo(), { disabled: !state.past.length })}
        {ib('redo', `${t('editor.redo')} (Ctrl+Y)${state.future[0] ? `: ${state.future[0].label}` : ''}`, () => store.redo(), { disabled: !state.future.length })}
        {selRoom && doc.roomPlan ? (
          <>
            <span className="sep" />
            <span className="toolbar-ctx" title="Selektovan zid prostorije — alati za zid">
              {selRoom.pointId ? cornerLabel(doc.roomPlan, selRoom.pointId) : selRoomItem ? `${lineLabel(doc.roomPlan, selRoom.lineId)} › ${itemLabel(doc.roomPlan, selRoomItem)}` : lineLabel(doc.roomPlan, selRoom.lineId)}
            </span>
            {ROOM_ITEM_KINDS.map((k) => (
              <button
                key={k}
                className="btn sm ghost"
                disabled={!canEdit}
                title={`Dodaj na zid: ${ROOM_ITEM_LABEL[k]} (najslobodnije mjesto, mjere u svojstvima)`}
                onClick={() => {
                  const id = addItemToWall(store.exec.bind(store), k, selRoom.lineId);
                  if (id) store.select({ cabinetIds: [], partId: null, room: { lineId: selRoom.lineId, itemId: id } });
                }}
              >
                <span aria-hidden>{ROOM_ITEM_ICON[k]}</span> <span className="tb-label">{ROOM_ITEM_LABEL[k]}</span>
              </button>
            ))}
            <span className="sep" />
            <button className={`btn sm ${roomDims ? 'on' : 'ghost'}`} aria-pressed={roomDims} onClick={toggleRoomDims} title="Kote: odmak elemenata od kraja zida i od poda (za elemente na selektovanom zidu)">
              ↔ Kote
            </button>
            {ib('view-top', 'Uredi tlocrt prostorije', () => setRoomOpen(true), { disabled: readOnly && !doc.roomPlan })}
            {ib('delete', `${selRoomItem ? `Obriši: ${itemLabel(doc.roomPlan, selRoomItem)}` : `Obriši zid (${lineLabel(doc.roomPlan, selRoom.lineId)})`} (Del)`, () => {
              removeRoomSelection(store.exec.bind(store), doc.roomPlan!, selRoom);
              store.select({ cabinetIds: [], partId: null, room: selRoom.itemId ? { lineId: selRoom.lineId } : null });
            }, { disabled: !canEdit || !!selRoom.pointId, danger: true })}
            <button className="btn sm ghost" onClick={() => store.select({ cabinetIds: [], partId: null })} title="Nazad na alate za elemente (Esc)">
              ✕
            </button>
          </>
        ) : (
          <>
        <span className="sep" />
        {ib('add-cabinet', 'Element iz kataloga', () => setLeftTab(leftTab === 'library' ? 'scene' : 'library'), { on: leftTab === 'library' })}
        {ib('add-panel', 'Nova ploča (u selektovani sklop ili novi sklop)', actions.addPanel, { disabled: !canEdit })}
        {ib('add-divider', frontUnit ? 'Pregrada: presijeci prostor novom pločom' : 'Pregrada — izaberite sklop od ploča', () => (dividerPick ? cancelDivider() : startDivider(false)), { on: !!dividerPick, disabled: insideNeedsUnit })}
        {ib('add-shelves', frontUnit ? 'Police: kliknite prostor u koji idu' : 'Police — izaberite sklop od ploča', () => (regionPick ? cancelRegionPick() : startShelves()), { on: !!regionPick, disabled: insideNeedsUnit })}
        {ib('add-drawer', frontUnit ? 'Ladice (klizači 13 mm, zazor gore/dolje 10 mm)' : 'Ladice — izaberite sklop od ploča', () => (drawerPick ? cancelDrawers() : startDrawers()), { on: !!drawerPick, disabled: insideNeedsUnit })}
        {ib('add-front', frontUnit ? 'Fronta preko jednog ili više prostora' : 'Fronta — izaberite sklop od ploča', () => (frontPick ? cancelFrontPick() : startFront(false)), { on: !!frontPick, disabled: insideNeedsUnit })}
        <span className="sep" />
        {ib('preset', 'Preset iz selekcije', () => setWizardOpen(true), { disabled: !canEdit || !hasSel })}
        {ib('duplicate', `${t('editor.duplicate')} (Ctrl+D)`, actions.duplicate, { disabled: !canEdit || !hasSel })}
        {ib('mirror', t('editor.mirror'), actions.mirror, { disabled: !canEdit || !hasSel })}
        {ib('rotate-90', 'Rotacija 90°', actions.rotate, { disabled: !canEdit || !hasSel })}
        {ib('hide', t('editor.hide'), () => actions.toggle('hidden'), { disabled: !canEdit || !hasSel })}
        {ib('lock', t('editor.lock'), () => actions.toggle('locked'), { disabled: !canEdit || !hasSel })}
        {ib('delete', `${t('editor.delete')} (Del)`, actions.remove, { disabled: !canEdit || !hasSel, danger: true })}
        <span className="sep" />
        {ib('move', 'Gizmo za pomjeranje (G). Držite S za snap.', () => setGizmoOn(!gizmoOn), { on: gizmoOn })}
        {ib('link', 'Linkuj mjere (L). Ploče birajte Ctrl+klikom, elemente Shift+klikom.', () => setLinkOpen(true), { disabled: !canEdit || linkCandidates(doc, selection).length < 2 })}
          </>
        )}
      </div>
      <div className="workspace">
        <aside className="panel panel-left" aria-label={t('editor.scene')}>
          <div className="panel-tabs" role="tablist">
            <button className={leftTab === 'scene' ? 'on' : ''} onClick={() => setLeftTab('scene')}>
              {t('editor.scene')} ({doc.cabinets.length + doc.assemblies.length})
            </button>
            <button className={leftTab === 'library' ? 'on' : ''} onClick={() => setLeftTab('library')}>
              Katalog
            </button>
          </div>
          {leftTab === 'library' ? (
            <>
              {insertError && <p className="error-text" style={{ padding: '0 8px' }}>{insertError}</p>}
              <LibraryPanel presets={presets} error={presetsError} disabled={!canEdit} onInsert={(p) => void actions.insertPreset(p)} />
            </>
          ) : (
            <SceneTree doc={doc} ev={ev} selection={selection} store={store} canEdit={canEdit} hidden={hidden} onToggleHidden={toggleHidden} />
          )}
        </aside>
        <div className="viewport" ref={hostRef}>
          <div className="vp-viewbar" role="toolbar" aria-label="Pogled">
            {ib('perspective', projection === 'perspective' ? 'Perspektiva — klik: ortogonalno' : 'Ortogonalno — klik: perspektiva', flipProjection, { on: projection === 'ortho' })}
            <span className="vp-viewbar-sep" />
            {VIEWS.map((x) => (
              <span key={x.v} className={x.v === 'bottom' ? 'ico-flip' : undefined}>
                {ib(x.icon, x.key ? `${x.label} (${x.key})` : x.label, () => vpRef.current?.setView(x.v))}
              </span>
            ))}
            {ib('views-more', `${t('editor.frame')} (F)`, () => vpRef.current?.frameSelection())}
            <span className="vp-viewbar-sep" />
            {DISPLAY.map((x) => (
              <span key={x.k}>{ib(x.icon, `${x.label}${x.key ? ` (${x.key})` : ''}${x.title ? ` — ${x.title}` : ''}`, () => toggle(x.k), { on: !!opts[x.k] })}</span>
            ))}
          </div>
          <div className="vp-actions" hidden={linkOpen}>
            {slopeFit && 'remove' in slopeFit && (
              <div className="vp-card vp-card-ask" role="group" aria-label="Kosina zida">
                <div className="vp-card-text">
                  <strong>Kosina više nije potrebna</strong>
                  <span>element više ne udara u kosinu zida — isključiti kosinu na sklopu?</span>
                </div>
                <button
                  className="btn sm primary"
                  onClick={() =>
                    store.exec('Isključena kosina (nije potrebna)', (d) => {
                      const a = d.assemblies.find((x) => x.id === selAsm!.id);
                      if (a) delete a.slope;
                    })
                  }
                >
                  Isključi kosinu
                </button>
                <button
                  className="btn sm ghost"
                  title="Zadrži kosinu (više se ne pita)"
                  onClick={() =>
                    store.exec('Kosina zadržana', (d) => {
                      const a = d.assemblies.find((x) => x.id === selAsm!.id);
                      if (a?.slope) delete a.slope.fromWall;
                    })
                  }
                >
                  Zadrži
                </button>
              </div>
            )}
            {slopeFit && 'slope' in slopeFit && doc.roomPlan && (
              <div className="vp-card vp-card-ask" role="group" aria-label="Kosina zida">
                <div className="vp-card-text">
                  <strong>{selAsm?.slope ? 'Kosina se promijenila' : 'Element udara u kosinu'}</strong>
                  <span>
                    {lineLabel(doc.roomPlan, slopeFit.lineId)}: vrh elementa je iznad kosine zida — prilagodi sklop tačno kosini
                  </span>
                </div>
                <button
                  className="btn sm primary"
                  onClick={() =>
                    store.exec('Sklop pod kosinom zida', (d) => {
                      const a = d.assemblies.find((x) => x.id === selAsm!.id);
                      if (a) a.slope = slopeFit.slope;
                    })
                  }
                >
                  ◺ Prilagodi kosini
                </button>
              </div>
            )}
            {trimSwap && (
              <div className="vp-card vp-card-copy" role="group" aria-label="Preklop blendi">
                <div className="vp-card-text">
                  <strong>Ugao blendi</strong>
                  <span>{trimSwap.text}</span>
                </div>
                <button
                  className="btn sm primary"
                  onClick={() =>
                    store.exec('Zamjena preklopa blendi', (d) => {
                      const a = d.assemblies.find((x) => x.id === trimSwap.unitId);
                      if (a) swapTrimJoint(a, trimSwap.corner);
                    })
                  }
                >
                  ⇄ Zamijeni preklop
                </button>
              </div>
            )}
            {selRoom && !selRoomItem && !selRoom.pointId && doc.roomPlan && canEdit && (
              <div className="vp-card vp-card-copy" role="group" aria-label="Opcije zida">
                <div className="vp-card-text">
                  <strong>{lineLabel(doc.roomPlan, selRoom.lineId)}</strong>
                  <span>debljina na drugu stranu linije — unutrašnja strana postaje vanjska</span>
                </div>
                <button className="btn sm primary" onClick={() => roomEdit(store.exec.bind(store), 'Okretanje zida', (p) => flipRoomWall(p, selRoom.lineId))}>
                  ⇄ Okreni zid
                </button>
              </div>
            )}
            {(canMount || wallMount) && (
              <div className="vp-card vp-card-copy" role="group" aria-label="Montiraj na zid">
                <div className="vp-card-text">
                  <strong>{wallMount ? 'Kliknite zid' : 'Uz zid'}</strong>
                  <span>{wallMount ? 'leđa elementa idu na zid, sam se okreće i bira slobodno mjesto (Esc prekida)' : 'zalijepi element leđima na zid prostorije'}</span>
                </div>
                {wallMount ? (
                  <button className="btn sm" onClick={() => setWallMount(null)}>
                    Otkaži
                  </button>
                ) : (
                  <button className="btn sm primary" onClick={() => (setMountNote(null), setWallMount(selUnit!.id))} title="Izaberite zid; element se okrene leđima prema zidu i stane na najslobodnije mjesto (sredina slobodnog zida)">
                    Montiraj na zid
                  </button>
                )}
              </div>
            )}
            {mountNote && (
              <div className="vp-card vp-card-ask" role="status">
                <div className="vp-card-text">
                  <strong>Montaža na zid</strong>
                  <span>{mountNote}</span>
                </div>
                <button className="btn sm ghost" onClick={() => setMountNote(null)}>
                  ✕
                </button>
              </div>
            )}
            {connected && (
              <div className="vp-card vp-card-ask" key={`c-${state.past.length}`} role="group" aria-label="Ažuriranje spojenih ploča">
                <div className="vp-card-text">
                  <strong>Pomjeriti spojene ploče?</strong>
                  <span title={connected.map((m) => m.label).join(', ')}>
                    {connected.length === 1 ? connected[0]!.label : `${connected.length} ploče`} — prate novu mjeru
                  </span>
                </div>
                <button className="btn sm primary" onClick={() => store.exec('Ažuriranje spojenih', (d) => applyConnectedMoves(d, connected))}>
                  Update connected
                </button>
              </div>
            )}
            {drawerPick && (
              <div className="vp-card vp-card-copy" role="group" aria-label="Nove ladice">
                <div className="vp-card-text">
                  <strong>{drawerPick.selected === null ? 'Ladice: izaberite prostor' : 'Ladice'}</strong>
                  <span>{drawerPick.selected === null ? 'prostor s bočnim stranicama, pločom ispod i iznad (crveno = nije moguće)' : `${((drawerPick.spaces[drawerPick.selected]!.rect.x1 - drawerPick.spaces[drawerPick.selected]!.rect.x0) / 1000).toFixed(1)} × ${((drawerPick.spaces[drawerPick.selected]!.rect.y1 - drawerPick.spaces[drawerPick.selected]!.rect.y0) / 1000).toFixed(1)} mm`}</span>
                </div>
                {drawerPick.selected !== null &&
                  (() => {
                    const asm = doc.assemblies.find((a) => a.id === drawerPick.unitId);
                    const shelfSet = asm ? shelvesInOpening(asm, drawerPick.spaces[drawerPick.selected]!.ref) : null;
                    const fixedCount = shelfSet && drawerPick.shelves === 'shelvesAsDividers';
                    return (
                      <div className="drawer-pick">
                        {shelfSet && (
                          <div className="drawer-pick-q">
                            <span className="warn-line">U prostoru su police ({shelfSet.count}) — bez kvačice ladice ih zamjenjuju.</span>
                            <label title="Bez kvačice: police se brišu i zamjenjuju ladicama">
                              <input type="checkbox" checked={drawerPick.shelves === 'shelvesAsDividers'} onChange={(e) => setDrawerPick({ ...drawerPick, shelves: e.target.checked ? 'shelvesAsDividers' : 'replace' })} /> Police postaju pregrade — ladica u svaki razmak ({shelfSet.count + 1})
                            </label>
                          </div>
                        )}
                        <div className="drawer-pick-row">
                          {!fixedCount && (
                            <>
                              <span className="faint" style={{ fontSize: 12 }}>Broj</span>
                              <input
                                className="input"
                                type="number"
                                min={1}
                                max={12}
                                style={{ width: 52, height: 24 }}
                                value={drawerPick.count}
                                aria-label="Broj ladica"
                                onKeyDown={(e) => e.stopPropagation()}
                                onChange={(e) => setDrawerPick({ ...drawerPick, count: Math.max(1, Math.min(12, Math.round(Number(e.target.value)) || 1)) })}
                              />
                              {drawerPick.count > 1 && (
                                <label className="faint" style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                                  <input type="checkbox" checked={drawerPick.dividers} onChange={(e) => setDrawerPick({ ...drawerPick, dividers: e.target.checked })} /> Pregrade između ladica
                                </label>
                              )}
                            </>
                          )}
                          <button className="btn sm primary" onClick={createDrawers}>
                            Kreiraj
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                <button className="btn sm ghost" onClick={cancelDrawers}>
                  Otkaži
                </button>
              </div>
            )}
            {dividerPick && (
              <div className={`vp-card ${dividerPick.error ? 'vp-card-ask' : 'vp-card-copy'}`} role="group" aria-label="Nova pregrada">
                <div className="vp-card-text">
                  <strong>
                    {!dividerPick.spaces.length
                      ? 'Nema otvorenog prostora'
                      : !dividerPick.selected.length
                        ? 'Pregrada: izaberite prostor'
                        : `Pregrada: ${dividerPick.selected.length} ${dividerPick.selected.length === 1 ? 'prostor' : 'prostora'} · kako presjeći?`}
                  </strong>
                  <span className={dividerPick.error ? 'warn-line' : undefined}>
                    {dividerPick.error ?? (dividerPick.selected.length ? `${dividerSize(dividerPick)} · ploča ide na sredinu · Ctrl+klik dodaje` : 'klik bira, Ctrl+klik dodaje prostore')}
                  </span>
                </div>
                {(doc.assemblies.find((a) => a.id === dividerPick.unitId)?.shelfSets ?? []).length > 0 && !dividerPick.selected.length && (
                  <label className="opt" title="Svaki razmak između polica je poseban prostor">
                    <input type="checkbox" checked={dividerPick.betweenShelves} onChange={(e) => startDivider(e.target.checked)} /> Između polica
                  </label>
                )}
                {dividerPick.selected.length > 0 && (
                  <>
                    <span className="faint" style={{ fontSize: 12 }}>Broj</span>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={20}
                      style={{ width: 48, height: 24 }}
                      value={dividerPick.count}
                      aria-label="Broj pregrada"
                      title="Pregrade se raspoređuju ravnomjerno kroz prostor"
                      onKeyDown={(e) => e.stopPropagation()}
                      onChange={(e) => setDividerPick({ ...dividerPick, count: Math.max(1, Math.min(20, Math.round(Number(e.target.value)) || 1)), error: undefined })}
                    />
                    <button className="btn sm primary" onClick={() => createDivider('vertical')} title="Uspravna pregrada (dijeli lijevo / desno)">
                      ┃ Vertikalno
                    </button>
                    <button className="btn sm primary" onClick={() => createDivider('horizontal')} title="Vodoravna ploča (dijeli gore / dolje)">
                      ━ Horizontalno
                    </button>
                  </>
                )}
                <button className="btn sm ghost" onClick={cancelDivider}>
                  Otkaži
                </button>
              </div>
            )}
            {frontPick && (
              <div className={`vp-card ${frontPreview?.irregular || frontPick.error ? 'vp-card-ask' : 'vp-card-copy'}`} role="group" aria-label="Nova fronta">
                {(() => {
                  const { drawers, plain, groups } = frontGroups(frontPick);
                  const n = frontPick.selected.length;
                  return (
                    <div className="vp-card-text">
                      <strong>
                        {!frontPick.spaces.length
                          ? 'Nema otvorenog prostora'
                          : !n
                            ? 'Fronta: izaberite prostor'
                            : `${n} ${n === 1 ? 'prostor' : 'prostora'} → ${groups.length} ${groups.length === 1 ? 'fronta' : groups.length < 5 ? 'fronte' : 'fronti'}`}
                      </strong>
                      {frontPick.error ? (
                        <span className="warn-line">{frontPick.error}</span>
                      ) : (
                        <>
                          {drawers.length > 1 || (drawers.length && plain.length) ? <span className="warn-line">Fronte ladica su uvijek odvojene — svaka ladica dobija svoju frontu ({drawers.length}).</span> : null}
                          <span className={frontPreview?.irregular ? 'warn-line' : undefined}>
                            {frontPreview?.irregular
                              ? 'Nepravilan oblik — ide na CNC obradu, mjere posebno u tabeli'
                              : frontPreview
                                ? `${((frontPreview.bbox.x1 - frontPreview.bbox.x0) / 1000).toFixed(1)} × ${((frontPreview.bbox.y1 - frontPreview.bbox.y0) / 1000).toFixed(1)} mm · Ctrl+klik dodaje`
                                : 'klik bira, Ctrl+klik dodaje prostore'}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })()}
                {frontGroups(frontPick).plain.length > 1 && (
                  <label className="opt" title="Svaki izabrani prostor dobija svoju frontu (fuga između); bez kvačice: jedna fronta preko svih">
                    <input type="checkbox" checked={!frontPick.together} onChange={(e) => setFrontPick({ ...frontPick, together: !e.target.checked, error: undefined })} /> Svaki prostor zasebna fronta
                  </label>
                )}
                {(doc.assemblies.find((a) => a.id === frontPick.unitId)?.shelfSets ?? []).length > 0 && (
                  <label className="opt" title="Svaki razmak između polica je poseban prostor">
                    <input type="checkbox" checked={frontPick.betweenShelves} onChange={(e) => startFront(e.target.checked)} /> Između polica
                  </label>
                )}
                <button className="btn sm primary" disabled={!frontPick.selected.length} onClick={createFront}>
                  Kreiraj
                </button>
                <button className="btn sm ghost" onClick={cancelFrontPick}>
                  Otkaži
                </button>
              </div>
            )}
            {selFront && selFront.front.hinge !== null && !selFront.front.spaces.some((sp) => sp.drawer) && !frontPick && !dividerPick && !selFront.asm.locked && (
              <div className="vp-card vp-card-link" key={`split-${selFront.front.id}`} role="group" aria-label="Podjela fronte">
                <div className="vp-card-text">
                  <strong>Podijeli frontu</strong>
                  <span>{selFront.front.label}</span>
                </div>
                <button className="btn sm" onClick={() => doSplitFront(selFront.asm.id, selFront.front.id, 'vertical')} title="Lijeva i desna fronta, fuga između">
                  ┃ Lijevo / desno
                </button>
                <button className="btn sm" onClick={() => doSplitFront(selFront.asm.id, selFront.front.id, 'horizontal')} title="Donja i gornja fronta, fuga između">
                  ━ Gore / dolje
                </button>
              </div>
            )}
            {shelfFit && !frontPick && !dividerPick && (
              <div className="vp-card vp-card-ask" key={`fit-${shelfFit.hits.length}`} role="group" aria-label="Prilagođavanje polica">
                <div className="vp-card-text">
                  <strong>Unutarnja fronta udara u police ({shelfFit.hits.length})</strong>
                  <span>Automatski prilagoditi dužinu polica?</span>
                </div>
                <button className="btn sm primary" onClick={fitShelves} title="Skraćuje samo police koje se sada sudaraju s frontom, do njene zadnje strane">
                  Da, skrati police
                </button>
                <button className="btn sm ghost" onClick={() => setShelfFitDismissed(doc)}>
                  Ne
                </button>
              </div>
            )}
            {issueFocus && (
              <div className={`vp-card ${issueFocus.severity === 'info' ? 'vp-card-link' : 'vp-card-ask'}`} role="group" aria-label="Problem i rješenja">
                <div className="vp-card-text">
                  <strong>{issueFocus.severity === 'error' ? 'Greška' : issueFocus.severity === 'warning' ? 'Upozorenje' : 'Info'}</strong>
                  <span className="issue-msg">{issueFocus.message}</span>
                  {!fixes.length && <span className="faint">{issueFocus.cabinetId ? 'Nema automatskog rješenja — podesite u svojstvima desno.' : 'Nema automatskog rješenja.'}</span>}
                </div>
                <div className="issue-fixes">
                  {fixes.map((f) => (
                    <button key={f.id} className="btn sm" title={f.title} onClick={() => store.exec(f.label, (d) => void f.apply(d))}>
                      {f.label}
                    </button>
                  ))}
                </div>
                <button className="btn sm ghost" onClick={closeIssue} title="Zatvori (crveni okvir nestaje)" aria-label="Zatvori">
                  ✕
                </button>
              </div>
            )}
            {frontWall && !frontPick && (
              <div className={`vp-card ${'error' in frontWall ? 'vp-card-ask' : 'vp-card-link'}`} role="group" aria-label="Preklop fronte i zida">
                <div className="vp-card-text">
                  <strong>
                    {frontWall.front.label} + {frontWall.panel.label}
                  </strong>
                  <span className={'error' in frontWall ? 'warn-line' : undefined}>
                    {'error' in frontWall ? frontWall.error : frontWall.state === 'front' ? 'prolazi: fronta (preko zida)' : 'prolazi: zid (preko ivice fronte)'}
                  </span>
                </div>
                {!('error' in frontWall) && (
                  <button
                    className="btn sm"
                    title="Samo na ovoj strani: zid ide naprijed preko ivice fronte, ili fronta opet preko zida"
                    onClick={() =>
                      store.exec(
                        'Zamjena preklopa fronte i zida',
                        (d) => {
                          const a = d.assemblies.find((x) => x.id === frontWall.unitId);
                          if (a) swapFrontWall(a, frontWall.front.id, frontWall.panel.key);
                        },
                        { pins: false },
                      )
                    }
                  >
                    ⇄ Zamijeni preklop
                  </button>
                )}
              </div>
            )}
            {selFront && selFront.front.hinge === null && !frontPick && (
              <div className="vp-card vp-card-ask" key={`h-${selFront.front.id}`} role="group" aria-label="Strana šarki">
                <div className="vp-card-text">
                  <strong>Šarke: na koju stranu?</strong>
                  <span>{selFront.front.label}</span>
                </div>
                {HINGES.map((h) => (
                  <button
                    key={h}
                    className={`btn sm ${h === 'none' ? 'ghost' : ''}`}
                    onClick={() =>
                      store.exec('Šarke fronte', (d) => {
                        const f = d.assemblies.find((a) => a.id === selFront.asm.id)?.fronts?.find((x) => x.id === selFront.front.id);
                        if (f) f.hinge = h;
                      })
                    }
                  >
                    {HINGE_LABEL[h]}
                  </button>
                ))}
              </div>
            )}
            {centerOptions.length > 0 && !regionPick && !frontPick && !dividerPick && !drawerPick && (
              <div className="vp-card vp-card-center" key={`c-${moveTarget?.kind === 'panel' ? moveTarget.partId : moveTarget?.unitId}`} role="group" aria-label="Centriranje">
                <div className="vp-card-text">
                  <strong>Centriranje</strong>
                  <span>sredina ploče u prostoru</span>
                </div>
                <div className="center-rows">
                  {centerOptions.map((o) => {
                    const AX = ['X', 'Y', 'Z'][o.axis];
                    const val = centerPct[o.axis] ?? '50';
                    const pct = Number(val.replace(',', '.'));
                    const ok = Number.isFinite(pct) && pct >= 0 && pct <= 100;
                    return (
                      <div key={o.axis} className="center-row" title={`${o.label} · sada ${o.pct}%`}>
                        <span className={`ax ax-${o.axis}`}>{AX}</span>
                        <input
                          className={`input${ok ? '' : ' invalid'}`}
                          value={val}
                          aria-label={`Postotak ${AX}`}
                          onChange={(e) => setCenterPct({ ...centerPct, [o.axis]: e.target.value })}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter' && ok) applyCenter(o.axis, pct);
                          }}
                        />
                        <span className="faint">%</span>
                        <button className="btn sm" disabled={!ok} onClick={() => applyCenter(o.axis, pct)}>
                          {pct === 50 ? 'Center' : 'Pomjeri'}
                        </button>
                        <span className="faint now">sada {o.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {copyDim && (
              <div className="vp-card vp-card-copy" key={`e-${JSON.stringify(edgePick.target)}`} role="group" aria-label="Kopiraj dimenziju">
                <div className="vp-card-text">
                  <strong>{formatMm(copyDim.value)} mm → {copyDim.label}</strong>
                  <span title={copyDim.blocked ?? undefined}>{copyDim.blocked ?? `${copyDim.prop} sada ${formatMm(copyDim.current)} mm`}</span>
                </div>
                <button className="btn sm primary" disabled={!!copyDim.blocked || copyDim.value === copyDim.current} onClick={copyDim.apply}>
                  Kopiraj dimenziju
                </button>
              </div>
            )}
            {linkItems.length >= 2 && (
              <div className="vp-card vp-card-link" role="group" aria-label="Link">
                <div className="vp-card-text">
                  <strong>{linkItems.length} izabrano</strong>
                  <span>{overlap ? overlap.text : 'poveži mjere'}</span>
                </div>
                {shelfItems ? (
                  <>
                    <button className="btn sm" onClick={() => linkEqual(store.exec.bind(store), shelfItems, ['thickness'], 'Link debljina polica')}>
                      Link debljina
                    </button>
                    <button className="btn sm" onClick={() => linkEqual(store.exec.bind(store), shelfItems, ['gap'], 'Link zazor polica')}>
                      Link zazor
                    </button>
                    <button className="btn sm" onClick={() => linkEqual(store.exec.bind(store), shelfItems, ['count'], 'Link broj polica')}>
                      Link broj
                    </button>
                    <button className="btn sm primary" onClick={() => linkEqual(store.exec.bind(store), shelfItems, SHELF_PROPS, 'Link police (sve)')} title="Broj polica, zazor i debljina">
                      Link all
                    </button>
                  </>
                ) : (
                  <button className="btn sm" onClick={() => setLinkOpen(true)} title="Linkuj mjere (L)">
                    🔗 Link
                  </button>
                )}
                {overlap && (
                  <button className="btn sm" onClick={overlap.apply} title="Zamijeni koja ploča prelazi preko koje na ovom spoju">
                    ⇄ Zamijeni preklop
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="viewport-overlay">
            {opts.edges && (
              <div className="legend">
                <span>
                  <i style={{ background: 'var(--edge-band)' }} />
                  kant
                </span>
                <span>
                  <i style={{ background: 'var(--edge-unresolved)' }} />
                  neriješeno
                </span>
                <span>
                  <i style={{ background: 'var(--info)' }} />
                  zaptivanje
                </span>
                <span>
                  <i style={{ background: 'var(--ok)' }} />
                  fabrička
                </span>
              </div>
            )}
            {opts.openFronts && <span className="legend">Pregled otvaranja (ne mijenja proizvodnu geometriju)</span>}
            {snapInfo && <span className="legend">Snap · {snapInfo}</span>}
            {cornerState === 'selected' && <span className="legend">Ugao izabran — kliknite ugao druge ploče da se poklope (Esc prekida)</span>}
            {drawerPick && <span className="legend vp-ask">Ladice: kliknite prostor (Esc prekida)</span>}
            {dividerPick && dividerPick.spaces.length > 0 && <span className="legend vp-ask">Pregrada: klik bira prostor, Ctrl+klik dodaje prostore, pa izaberite smjer presjeka (Esc prekida)</span>}
            {frontPick && frontPick.spaces.length > 0 && <span className="legend vp-ask">Izaberite prostore za frontu — klik bira, Ctrl+klik dodaje (Esc prekida)</span>}
            {regionPick && <span className="legend vp-ask">Police: kliknite prostor (Esc prekida)</span>}
            {edgePick.source && !edgePick.target && <span className="legend">Ivica izabrana — kliknite ivicu bilo koje ploče (Esc prekida)</span>}
            {dupDrag && <span className="legend">Alt · kopija — original ostaje na mjestu</span>}
            {pinState === 'moving' && <span className="legend">Pin: približite ugao ili sredinu ivice — klik potvrđuje, Esc poništava</span>}
            {opts.links && doc.links.length > 0 && (
              <span className="legend">
                <span>
                  <i className="link-swatch equal" /> link
                </span>
                <span>
                  <i className="link-swatch offset" /> link sa razlikom
                </span>
              </span>
            )}
          </div>
        </div>
        <aside className="panel panel-right" aria-label={t('editor.properties')}>
          <div className="panel-head">
            <h3>{t('editor.properties')}</h3>
          </div>
          {/* Only the selected thing: a component (panel, shelf, drawer part, front), a group from the
              outliner (shelves, drawers, fronts), or the whole element. */}
          {selRoom ? (
            <RoomProperties doc={doc} sel={selRoom} exec={store.exec.bind(store)} disabled={!canEdit} onSelect={(room) => store.select({ cabinetIds: [], partId: null, room })} />
          ) : selPart && selEval ? (
            <>
              <PartProperties part={selPart} cab={selEval} exec={store.exec.bind(store)} disabled={!canEdit || selUnit!.locked} />
              {selFront ? (
                <>
                  <FrontProperties key={selFront.front.id} inst={selFront.asm} front={selFront.front} exec={store.exec.bind(store)} disabled={!canEdit} onRemoved={() => store.select({ cabinetIds: [selFront.asm.id], partId: null })} onSplit={(dir) => doSplitFront(selFront.asm.id, selFront.front.id, dir)} />
                  {/* A drawer front: the drawer behind it too. */}
                  {(() => {
                    const ref = selFront.front.spaces.find((x) => x.drawer)?.drawer;
                    const set = ref && selFront.asm.drawerSets?.find((x) => x.id === ref.setId);
                    return set ? <DrawerProperties key={`${set.id}-${ref.index}`} inst={selFront.asm} set={set} index={ref.index} exec={store.exec.bind(store)} disabled={!canEdit} onRemoved={() => store.select({ cabinetIds: [selFront.asm.id], partId: null })} /> : null;
                  })()}
                </>
              ) : selAsm && selDrawer ? (
                <DrawerProperties key={selDrawer.set.id} inst={selAsm} set={selDrawer.set} index={selDrawer.index} exec={store.exec.bind(store)} disabled={!canEdit} onRemoved={() => store.select({ cabinetIds: [selAsm.id], partId: null })} />
              ) : selAsm && selShelf ? (
                <>
                  <ShelfProperties
                    key={selShelf.set.id}
                    inst={selAsm}
                    set={selShelf.set}
                    shelfIndex={selShelf.index}
                    exec={store.exec.bind(store)}
                    disabled={!canEdit}
                    onSelectSet={() => store.select({ cabinetIds: [selAsm.id], partId: null, shelfSetId: selShelf.set.id })}
                    onRemoved={() => store.select({ cabinetIds: [selAsm.id], partId: null })}
                  />
                  <LinksSection doc={doc} unitId={selAsm.id} setId={selShelf.set.id} exec={store.exec.bind(store)} disabled={!canEdit} />
                </>
              ) : selAsm && selAsm.slope && selPart.localKey.startsWith('kosina-') ? (
                <SlopeProperties inst={selAsm} exec={store.exec.bind(store)} disabled={!canEdit || selAsm.locked} />
              ) : selAsm && selAsm.legs && isLegPart(selPart.localKey) ? (
                <LegProperties inst={selAsm} exec={store.exec.bind(store)} disabled={!canEdit || selAsm.locked} />
              ) : selAsm && trimOfPart(selAsm, selPart.localKey) ? (
                <TrimProperties inst={selAsm} exec={store.exec.bind(store)} disabled={!canEdit || selAsm.locked} only={trimOfPart(selAsm, selPart.localKey)!} />
              ) : selAsm ? (
                <>
                  <LinksSection doc={doc} unitId={selAsm.id} panelKey={panelKeyOfPart(selAsm, selPart.localKey)} exec={store.exec.bind(store)} disabled={!canEdit} />
                  <AssemblyProperties
                    key={selAsm.id}
                    inst={selAsm}
                    partLocalKey={selPart.localKey}
                    view="panel"
                    exec={store.exec.bind(store)}
                    disabled={!canEdit}
                    onSelectPanel={(k) => store.select({ cabinetIds: [selAsm.id], partId: k ? `${selAsm.id}/${k}` : null })}
                  />
                </>
              ) : null}
            </>
          ) : selAsm && selection.frontGroup ? (
            <FrontGroupProperties inst={selAsm} exec={store.exec.bind(store)} disabled={!canEdit} />
          ) : selAsm && selDrawer ? (
            <DrawerProperties key={selDrawer.set.id} inst={selAsm} set={selDrawer.set} index={null} exec={store.exec.bind(store)} disabled={!canEdit} onRemoved={() => store.select({ cabinetIds: [selAsm.id], partId: null })} />
          ) : selAsm && selShelf ? (
            <>
              <ShelfProperties
                key={selShelf.set.id}
                inst={selAsm}
                set={selShelf.set}
                shelfIndex={null}
                exec={store.exec.bind(store)}
                disabled={!canEdit}
                onSelectSet={() => store.select({ cabinetIds: [selAsm.id], partId: null, shelfSetId: selShelf.set.id })}
                onRemoved={() => store.select({ cabinetIds: [selAsm.id], partId: null })}
              />
              <LinksSection doc={doc} unitId={selAsm.id} setId={selShelf.set.id} exec={store.exec.bind(store)} disabled={!canEdit} />
            </>
          ) : selCab ? (
            <>
              {selCab.preset && <PresetSection key={`${selCab.id}:${selCab.preset.versionId}`} inst={selCab} exec={store.exec.bind(store)} disabled={!canEdit || selCab.locked} presets={presets} />}
              <CabinetProperties key={selCab.id} inst={selCab} exec={store.exec.bind(store)} disabled={!canEdit} />
              <LinksSection doc={doc} unitId={selCab.id} exec={store.exec.bind(store)} disabled={!canEdit} />
            </>
          ) : selAsm ? (
            <>
              <AssemblyProperties
                key={selAsm.id}
                inst={selAsm}
                partLocalKey={null}
                view="assembly"
                exec={store.exec.bind(store)}
                disabled={!canEdit}
                onSelectPanel={(k) => store.select({ cabinetIds: [selAsm.id], partId: k ? `${selAsm.id}/${k}` : null })}
                onSaveToCatalog={canEdit ? () => setCatalogSave(selAsm.id) : undefined}
              />
              <LinksSection doc={doc} unitId={selAsm.id} exec={store.exec.bind(store)} disabled={!canEdit} />
            </>
          ) : selection.cabinetIds.length > 1 ? (
            <p className="muted" style={{ padding: 12 }}>
              Selektovano {selection.cabinetIds.length} elemenata. Strelice pomjeraju (Shift = 100 mm). „Preset iz selekcije” pravi statički sklop.
            </p>
          ) : (
            <>
              <p className="muted" style={{ padding: '12px 12px 0' }}>{t('editor.noSelection')}</p>
              <ProjectProperties doc={doc} exec={store.exec.bind(store)} disabled={!canEdit} />
            </>
          )}
        </aside>
        <BottomPanel
          doc={doc}
          ev={ev}
          projectId={loaded.project.id}
          savedRevision={state.baseRevision}
          dirty={state.doc !== state.serverDoc}
          onSelect={(cabinetId, partId) => store.select({ cabinetIds: [cabinetId], partId })}
          onIssue={focusIssue}
          focusedIssue={issueFocus}
          readOnly={readOnly}
          onDismiss={(list, dismiss) => {
            if (dismiss) closeIssue();
            store.exec(dismiss ? 'Odbaci upozorenje' : list.length > 1 ? 'Vrati odbačena upozorenja' : 'Vrati upozorenje', (d) => {
              const keys = new Set(d.dismissedIssues ?? []);
              for (const i of list) (dismiss ? keys.add(issueKey(i)) : keys.delete(issueKey(i)));
              if (keys.size) d.dismissedIssues = [...keys];
              else delete d.dismissedIssues;
            });
          }}
        />
      </div>

      {cornerMenu && (
        <div className="menu-list corner-menu" role="menu" style={{ left: cornerMenu.x, top: cornerMenu.y, zIndex: 70 }}>
          <button
            className="menu-item"
            role="menuitem"
            onClick={() => {
              vpRef.current?.pickCornerSource(cornerMenu.corner);
              setCornerMenu(null);
            }}
          >
            <span className="menu-mark" />
            <span className="menu-label">Izaberi ovu tačku (snap na drugu tačku)</span>
          </button>
          <button
            className="menu-item"
            role="menuitem"
            disabled={readOnly || !selAsm || selAsm.locked || selAsm.transform.mirrored || !!selection.partId}
            title={selAsm ? (selAsm.transform.mirrored ? 'Nije moguće kod preslikanog sklopa' : 'Gizmo i ishodište sklopa prelaze u ovu tačku; ploče ostaju gdje jesu') : 'Kod elemenata iz kataloga ishodište je fiksno'}
            onClick={() => {
              const id = selAsm!.id;
              const corner = cornerMenu.corner;
              store.exec('Ishodište sklopa u ugao', (d) => {
                const a = d.assemblies.find((x) => x.id === id);
                if (a) setAssemblyOriginAt(a, corner);
              });
              setCornerMenu(null);
            }}
          >
            <span className="menu-mark" />
            <span className="menu-label">Ishodište (gizmo) ovdje</span>
          </button>
        </div>
      )}
      {roomOpen && (
        <RoomEditor
          doc={doc}
          exec={store.exec.bind(store)}
          readOnly={readOnly}
          onClose={() => {
            setRoomOpen(false);
            setOpts((o) => ({ ...o, room: true }));
          }}
        />
      )}
      {linkOpen && <LinkDialog doc={doc} items={linkCandidates(doc, selection)} exec={store.exec.bind(store)} onClose={() => setLinkOpen(false)} />}

      {catalogSave && doc.assemblies.some((x) => x.id === catalogSave) && (
        <SaveToCatalog
          asm={doc.assemblies.find((x) => x.id === catalogSave)!}
          presets={presets}
          exec={store.exec.bind(store)}
          snapshot={() => vpRef.current?.snapshot() ?? Promise.resolve(null)}
          onClose={() => setCatalogSave(null)}
          onSaved={() => {
            setCatalogSave(null);
            setPresetsKey((k) => k + 1);
            setLeftTab('library');
          }}
        />
      )}
      {wizardOpen && (
        <PresetWizard
          units={ev.cabinets.filter((c) => selection.cabinetIds.includes(c.instance.id))}
          exec={store.exec.bind(store)}
          snapshot={() => vpRef.current?.snapshot() ?? Promise.resolve(null)}
          onClose={() => setWizardOpen(false)}
          onSaved={() => {
            setWizardOpen(false);
            setPresetsKey((k) => k + 1);
            setLeftTab('library');
          }}
        />
      )}

      {journal && (
        <div className="modal-backdrop">
          <div className="card modal" role="dialog" aria-modal>
            <h2>{t('editor.recoverTitle')}</h2>
            <p className="muted">
              Lokalna kopija od {new Date(journal.updatedAt).toLocaleString()} (bazirana na reviziji {journal.baseRevision}; server je na reviziji {loaded.revision}).
              {journal.baseRevision !== loaded.revision && ' Server je u međuvremenu izmijenjen — vraćanje će napraviti izmjenu preko novije revizije.'}
            </p>
            <div className="actions">
              <button
                className="btn"
                onClick={async () => {
                  await clearJournal(userId, loaded.project.id);
                  setJournal(null);
                }}
              >
                {t('editor.recoverDiscard')}
              </button>
              {journal.baseRevision !== loaded.revision && (
                <button
                  className="btn"
                  onClick={async () => {
                    const p = await Api.createProject(`${loaded.project.name} (lokalna kopija)`.slice(0, 200), loaded.project.client, journal.document);
                    await clearJournal(userId, loaded.project.id);
                    navigate(`/p/${p.id}`);
                  }}
                >
                  Snimi kao novi projekat
                </button>
              )}
              <button
                className="btn primary"
                disabled={readOnly}
                onClick={() => {
                  store.restoreFromJournal(migrateDocument(journal.document));
                  setJournal(null);
                }}
              >
                {t('editor.recoverApply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {state.save === 'conflict' && (
        <div className="modal-backdrop">
          <div className="card modal" role="alertdialog" aria-modal>
            <h2>{t('editor.conflictTitle')}</h2>
            <p className="muted">
              {t('editor.conflictText')} (vaša baza: rev {state.baseRevision}, server: rev {state.conflict?.headRevision})
            </p>
            <div className="actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <button className="btn" onClick={() => void store.loadServerVersion()}>
                {t('editor.conflictLoad')}
              </button>
              <button
                className="btn primary"
                onClick={async () => {
                  const id = await store.saveAsCopy(`${loaded.project.name} (moja verzija)`.slice(0, 200));
                  await clearJournal(userId, loaded.project.id);
                  navigate(`/p/${id}`);
                }}
              >
                {t('editor.conflictCopy')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </CatalogContext.Provider>
  );
}
